import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Stack,
  Avatar,
  Tooltip,
  Paper,
  Divider,
  Button,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Zoom,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputBase,
  ClickAwayListener,
  Grid,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  Collapse,
} from '@material-ui/core';
import {
  Close,
  Fullscreen,
  FullscreenExit,
  Send,
  Android,
  Person,
  Add,
  History,
  Settings,
  MoreVert,
  DeleteOutline,
  Edit,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  AutoAwesome,
  Image,
  Create,
  Search,
  AddCircleOutline,
  AddComment,
  Mic,
  ArrowUpward,
  Stop,
  CreateNewFolder,
  Language,
  TrackChanges,
  Brightness4,
  Brightness7,
  Forum,
  GraphicEq,
  Face,
  LockOutlined,
  Check,
  AttachFile,
  Videocam,
  Explore,
  MoreHoriz,
  FolderOpen,
  Restore,
  VisibilityOff,
  Memory,
  Schema,
  CropSquare,
  CropPortrait,
  StayCurrentPortrait,
  CropLandscape,
  Crop75,
  TrendingUp,
  RadioButtonUnchecked,
  ChatBubbleOutline,
} from '@material-ui/icons';

import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import robotIcon from 'src/assets/Ai Robot Vector Art.json';
import axios from 'axios';
import { REST_API } from 'src/constants/DefaultValues';
import {
  VOICE_STATES,
  voiceStateLabel,
  createAudioQueuePlayer,
  startPcmCapture,
  pcmToBase64,
} from '../voiceConversationUtils';

import {
  CHAT_GPT_BLUE,
  CHAT_GPT_DARK,
  CHAT_GPT_LIGHT_DARK,
  CHAT_GPT_AI_BG,
  MOBILE_BG,
  VIDEO_PROCESSING_STEPS,
  RECENT_FILES,
} from './constants';
import {
  getSefiUserId,
  sefiAuthHeaders,
  sefiRequestConfig,
  getLastChatSession,
  saveLastChatSession,
  clearLastChatSession,
  isRagImageFile,
  isRagDocumentFile,
  isVideoFile,
  isVideoUploadResult,
  isBlueprintUploadResult,
  mapUploadMetaToFile,
  uploadChatDocuments,
  uploadChatVideos,
  parseSseFrames,
  stripChatAsterisks,
  mapApiMessage,
  extractTicketRows,
} from './utils';
import { TemporaryChatIcon, VoiceWaveIcon } from './components/icons';
import ChatMessage from './components/ChatMessage';
import VoiceOnboarding from './components/VoiceOnboarding';
import TemporaryOnboarding from './components/TemporaryOnboarding';
import SidebarSessionList from './components/SidebarSessionList';
import DeleteProjectDialog from './components/DeleteProjectDialog';
import { SUGGESTIONS } from './suggestions';

export default function AiChatbot({ open, onClose }) {
  const theme = useTheme();
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const [maximized, setMaximized] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchModalQuery, setSearchModalQuery] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [searchHistoryLoading, setSearchHistoryLoading] = useState(false);
  const [recentsAnchor, setRecentsAnchor] = useState(null);
  const [isSelectingFile, setIsSelectingFile] = useState(false);
  const [isTemporary, setIsTemporary] = useState(false);
  const [isImageMode, setIsImageMode] = useState(false);
  const [isWriteMode, setIsWriteMode] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [hoverSearch, setHoverSearch] = useState(false);
  const [hoverImage, setHoverImage] = useState(false);
  const [aspectRatioAnchor, setAspectRatioAnchor] = useState(null);
  const [selectedRatio, setSelectedRatio] = useState('Auto');
  const [modelAnchor, setModelAnchor] = useState(null);
  const [selectedModel, setSelectedModel] = useState('Thinking');
  const [input, setInput] = useState('');
  const [plusAnchor, setPlusAnchor] = useState(null);
  const [recentFilesAnchor, setRecentFilesAnchor] = useState(null);
  const [sendMessage, setSendMessage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Voice-to-text states (mic — fills input only)
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Conversational voice (waveform button — full voice chat)
  const [voiceConversationState, setVoiceConversationState] = useState(VOICE_STATES.IDLE);
  const [voiceStatusDetail, setVoiceStatusDetail] = useState('');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const conversationRecorderRef = useRef(null);
  const conversationStreamRef = useRef(null);
  const conversationChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const voiceAbortRef = useRef(null);
  const ackTimerRef = useRef(null);
  const ackPlayedRef = useRef(false);
  const voiceActiveRef = useRef(false);
  const vadSessionIdRef = useRef(null);
  const pcmCaptureRef = useRef(null);
  const pcmPendingRef = useRef([]);
  const vadSendTimerRef = useRef(null);
  const vadAutoStopRef = useRef(false);
  const maxRecordTimerRef = useRef(null);
  const speechDetectedRef = useRef(false);
  const conversationalModeRef = useRef(false);
  const [isConversationalMode, setIsConversationalMode] = useState(false);
  const scheduleListenTimerRef = useRef(null);
  const startConversationRecordingRef = useRef(null);
  const scheduleNextListenRef = useRef(null);
  const exitConversationalModeRef = useRef(null);
  
  const recentFiles = RECENT_FILES;
  const exploreScrollRefMobile = useRef(null);
  const exploreScrollRefDesktop = useRef(null);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const isSelectingFileRef = useRef(false);
  const currentUploadTypeRef = useRef('document');
  const sendAbortRef = useRef(null);
  const activePlaceholderRef = useRef(null);
  const sendCancelledRef = useRef(false);

  useEffect(() => {
    const player = createAudioQueuePlayer();
    player.setOnStateChange((isPlaying) => {
      if (isPlaying) {
        setVoiceConversationState(VOICE_STATES.SPEAKING);
        setVoiceStatusDetail('Speaking…');
      } else if (voiceActiveRef.current) {
        voiceActiveRef.current = false;
        if (conversationalModeRef.current) {
          setVoiceStatusDetail('Listening for your next question…');
          scheduleNextListenRef.current?.();
        } else {
          setVoiceConversationState(VOICE_STATES.IDLE);
          setVoiceStatusDetail('');
        }
      }
    });
    audioPlayerRef.current = player;
    return () => player.stopAll();
  }, []);

  useEffect(() => {
    if (!open) {
      exitConversationalModeRef.current?.();
    }
  }, [open]);

  const resetFileSelectionState = useCallback(() => {
    isSelectingFileRef.current = false;
    setIsSelectingFile(false);
    chatInputRef.current?.focus();
  }, []);

  const openFilePicker = useCallback((type = 'document') => {
    currentUploadTypeRef.current = type;
    isSelectingFileRef.current = true;
    setIsSelectingFile(true);

    const onWindowFocus = () => {
      window.removeEventListener('focus', onWindowFocus);
      // If the user cancels the native picker, onChange won't fire — reset on focus return.
      setTimeout(() => {
        if (isSelectingFileRef.current) {
          resetFileSelectionState();
        }
      }, 500);
    };
    window.addEventListener('focus', onWindowFocus);

    fileInputRef.current?.click();
  }, [resetFileSelectionState]);
  // const [showVoiceOnboarding, setShowVoiceOnboarding] = useState(false);

  const uploadDocumentAttachment = async (fileItem, uploadType = 'document') => {
    const userId = getSefiUserId();
    const result = await uploadChatDocuments([fileItem], {
      sessionId: currentChatIdRef.current || currentChatId || null,
      userId,
      newSession: forceNewSessionRef.current,
      uploadType: uploadType,
    });
    if (result.sessionId && !isTemporary) {
      currentChatIdRef.current = result.sessionId;
      setCurrentChatId(result.sessionId);
      forceNewSessionRef.current = false;
    }
    return result;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const uploadType = currentUploadTypeRef.current;
    files.forEach((file) => {
      if (isVideoFile({ name: file.name, type: file.type, file })) {
        const id = Date.now() + Math.random();
        const item = {
          id,
          url: null,
          name: file.name,
          type: file.type,
          file,
          isVideo: true,
          uploading: false,
          uploaded: false,
        };
        setSelectedFiles((prev) => [...prev, item]);
        return;
      }
      if (file.type.startsWith('image/')) {
        if (!isRagImageFile({ name: file.name, type: file.type, file })) {
          return;
        }
        const id = Date.now() + Math.random();
        const reader = new FileReader();
        reader.onload = (ev) => {
          const item = {
            id,
            url: ev.target.result,
            name: file.name,
            type: file.type,
            file,
            uploading: true,
          };
          setSelectedFiles((prev) => [...prev, item]);
          uploadDocumentAttachment(item, uploadType)
            .then((result) => {
              const meta = (result.results || [])[0] || {};
              setSelectedFiles((prev) =>
                prev.map((f) => (f.id === id ? mapUploadMetaToFile(f, meta) : f))
              );
            })
            .catch((err) => {
              setSelectedFiles((prev) =>
                prev.map((f) =>
                  f.id === id
                    ? {
                        ...f,
                        uploading: false,
                        uploadError: err?.message || 'Upload failed',
                      }
                    : f
                )
              );
            });
        };
        reader.readAsDataURL(file);
        return;
      }
      const id = Date.now() + Math.random();
      const item = {
        id,
        url: null,
        name: file.name,
        type: file.type,
        file,
        uploading: isRagDocumentFile({ name: file.name, type: file.type, file }),
      };
      setSelectedFiles((prev) => [...prev, item]);

      if (isRagDocumentFile(item)) {
        uploadDocumentAttachment(item, uploadType)
          .then((result) => {
            const meta = (result.results || [])[0] || {};
            setSelectedFiles((prev) =>
              prev.map((f) => (f.id === id ? mapUploadMetaToFile(f, meta) : f))
            );
          })
          .catch((err) => {
            setSelectedFiles((prev) =>
              prev.map((f) =>
                f.id === id
                  ? {
                      ...f,
                      uploading: false,
                      uploadError: err?.message || 'Upload failed',
                    }
                  : f
              )
            );
          });
      }
    });
    e.target.value = '';
    // Delay resetting so ClickAway doesn't catch the focus return; refocus chat input
    setTimeout(resetFileSelectionState, 500);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const currentChatIdRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loadingChatId, setLoadingChatId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [projectSaving, setProjectSaving] = useState(false);
  const [expandedProjectKeys, setExpandedProjectKeys] = useState({});
  const [projectSessionsMap, setProjectSessionsMap] = useState({});
  const [projectSessionsLoadingKey, setProjectSessionsLoadingKey] = useState(null);
  const [deletingChatId, setDeletingChatId] = useState(null);
  const [deleteChatDialogOpen, setDeleteChatDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [deleteProjectError, setDeleteProjectError] = useState(null);
  const forceNewSessionRef = useRef(false);
  const selectedProjectIdRef = useRef(null);

  // Keep session id in a ref so consecutive sends (text/voice) reuse the same chat
  // even before React re-renders currentChatId — required for conversational memory.
  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  // Remember the last working chat across close/reopen (and page reload).
  useEffect(() => {
    if (currentChatId) {
      saveLastChatSession(currentChatId, selectedProjectId);
    }
  }, [currentChatId, selectedProjectId]);

  const [showVoiceOnboarding, setShowVoiceOnboarding] = useState(false);
  const [hasSeenVoiceOnboarding, setHasSeenVoiceOnboarding] = useState(() => {
    return localStorage.getItem('hasSeenVoiceOnboarding') === 'true';
  });

  const [showTemporaryOnboarding, setShowTemporaryOnboarding] = useState(false);
  const [hasSeenTemporaryOnboarding, setHasSeenTemporaryOnboarding] = useState(() => {
    return localStorage.getItem('hasSeenTemporaryOnboarding') === 'true';
  });

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const scrollRef = useRef(null);
  // Sticky bottom: only updated by user scroll / send / jump / chat switch — not by content growth alone.
  const stickToBottomRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const computeIsAtBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    const threshold = 80; // px — near bottom = stick + hide “scroll to latest”
    return el.scrollHeight - (el.scrollTop + el.clientHeight) <= threshold;
  }, []);

  const pinToBottom = useCallback((behavior = 'smooth') => {
    stickToBottomRef.current = true;
    setIsAtBottom(true);
    requestAnimationFrame(() => {
      scrollToBottom(behavior);
      // Follow-up after layout settles (images / loading bubble height).
      setTimeout(() => scrollToBottom(behavior === 'smooth' ? 'smooth' : 'auto'), 120);
    });
  }, [scrollToBottom]);

  const handleScroll = useCallback(() => {
    const atBottom = computeIsAtBottom();
    stickToBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
  }, [computeIsAtBottom]);

  const lastOpenRef = useRef(open);

  useEffect(() => {
    if (open && !lastOpenRef.current) {
      pinToBottom();
    }
    lastOpenRef.current = open;
  }, [open, pinToBottom]);

  // When switching conversation or folder, always jump to latest.
  useEffect(() => {
    if (!open) return;
    pinToBottom();
  }, [currentChatId, selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep scrolling to latest while generating / when messages update, if user is pinned to bottom.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      const behavior = el && (el.scrollHeight - el.scrollTop - el.clientHeight > 500) ? 'auto' : 'smooth';
      scrollToBottom(behavior);
    });
  }, [messages, sendMessage, scrollToBottom]);

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await axios.get(`${REST_API}ai/projects`, sefiRequestConfig());
      if (response?.data?.success && Array.isArray(response.data.projects)) {
        setProjects(response.data.projects);
      }
    } catch (err) {
      console.error('Failed to load chat projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  const mapApiSessions = useCallback(
    (sessions) =>
      (sessions || []).map((s) => ({
        id: s.id,
        title: s.title || 'New chat',
        projectId: s.project_id ?? null,
        messages: [],
      })),
    []
  );

  const projectCacheKey = (projectId) => (projectId == null ? 'all' : String(projectId));

  const fetchChatSessions = async (projectId = selectedProjectIdRef.current) => {
    const cacheKey = projectCacheKey(projectId);
    try {
      setHistoryLoading(true);
      setProjectSessionsLoadingKey(cacheKey);
      const config = sefiRequestConfig();
      const params = { ...(config.params || {}) };
      if (projectId) params.project_id = projectId;
      const response = await axios.get(`${REST_API}ai/sessions`, {
        headers: config.headers,
        params,
      });
      if (response?.data?.success && Array.isArray(response.data.sessions)) {
        const mapped = mapApiSessions(response.data.sessions);
        setHistory(mapped);
        setProjectSessionsMap((prev) => ({ ...prev, [cacheKey]: mapped }));
      }
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
    } finally {
      setHistoryLoading(false);
      setProjectSessionsLoadingKey(null);
    }
  };

  const toggleProjectExpanded = async (projectId) => {
    const cacheKey = projectCacheKey(projectId);
    const willOpen = !expandedProjectKeys[cacheKey];
    setExpandedProjectKeys((prev) => ({ ...prev, [cacheKey]: willOpen }));
    if (willOpen && !projectSessionsMap[cacheKey]) {
      await fetchChatSessions(projectId);
    }
  };

  const handleLoadChat = async (chat) => {
    if (!chat?.id) return;
    pinToBottom();
    if (chat.projectId) {
      setSelectedProjectId(chat.projectId);
      fetchChatSessions(chat.projectId);
    }
    setCurrentChatId(chat.id);
    currentChatIdRef.current = chat.id;
    forceNewSessionRef.current = false;
    setIsImageMode(false);
    setIsWriteMode(false);
    setIsSearchMode(false);
    setSelectedFiles([]);
    setInput('');
    setDrawerOpen(false);

    const hasCachedBotTimes =
      chat.messages?.length &&
      !chat.messages.some(
        (m) =>
          (m.role === 'ai' || m.role === 'assistant' || m.role === 'bot') &&
          m.generatedTime == null &&
          m.generated_time == null
      );

    if (chat.messages?.length && hasCachedBotTimes) {
      setMessages(chat.messages);
      setTimeout(() => pinToBottom(), 100);
      return;
    }

    try {
      setLoadingChatId(chat.id);
      const response = await axios.get(
        `${REST_API}ai/sessions/${chat.id}/messages`,
        sefiRequestConfig()
      );
      const loaded = (response?.data?.messages || [])
        .map(mapApiMessage)
        .filter(Boolean);
      setMessages(loaded);
      setHistory((prev) =>
        prev.map((item) =>
          item.id === chat.id ? { ...item, messages: loaded } : item
        )
      );
      setTimeout(() => pinToBottom(), 100);
    } catch (err) {
      console.error('Failed to load chat messages:', err);
      setMessages([]);
      const last = getLastChatSession();
      if (last?.sessionId === chat.id) {
        clearLastChatSession();
        currentChatIdRef.current = null;
        setCurrentChatId(null);
      }
    } finally {
      setLoadingChatId(null);
    }
  };

  const handleNewChat = () => {
    pinToBottom();
    forceNewSessionRef.current = true;
    currentChatIdRef.current = null;
    setCurrentChatId(null);
    clearLastChatSession();
    setMessages([]);
    setIsImageMode(false);
    setIsWriteMode(false);
    setIsSearchMode(false);
    setSelectedFiles([]);
    setInput('');
    setDrawerOpen(false);
  };

  const handleSelectProject = (projectId) => {
    pinToBottom();
    setSelectedProjectId(projectId);
    setExpandedProjectKeys((prev) => ({ ...prev, [String(projectId)]: true }));
    forceNewSessionRef.current = true;
    currentChatIdRef.current = null;
    setCurrentChatId(null);
    clearLastChatSession();
    setMessages([]);
    setInput('');
    setSelectedFiles([]);
    setDrawerOpen(false);
    fetchChatSessions(projectId);
  };

  const handleNewChatInProject = (projectId) => {
    pinToBottom();
    setSelectedProjectId(projectId);
    setExpandedProjectKeys((prev) => ({ ...prev, [String(projectId)]: true }));
    forceNewSessionRef.current = true;
    currentChatIdRef.current = null;
    setCurrentChatId(null);
    clearLastChatSession();
    setMessages([]);
    setIsImageMode(false);
    setIsWriteMode(false);
    setIsSearchMode(false);
    setSelectedFiles([]);
    setInput('');
    setDrawerOpen(false);
    fetchChatSessions(projectId);
  };

  const handleShowAllChats = () => {
    pinToBottom();
    setSelectedProjectId(null);
    forceNewSessionRef.current = true;
    currentChatIdRef.current = null;
    setCurrentChatId(null);
    clearLastChatSession();
    setMessages([]);
    setInput('');
    setDrawerOpen(false);
    fetchChatSessions(null);
  };

  const handleDeleteChat = (event, chat) => {
    event.stopPropagation();
    if (!chat?.id || deletingChatId) return;
    setChatToDelete(chat);
    setDeleteChatDialogOpen(true);
  };

  const handleCancelDeleteChat = () => {
    if (deletingChatId) return;
    setDeleteChatDialogOpen(false);
    setChatToDelete(null);
  };

  const handleConfirmDeleteChat = async () => {
    if (!chatToDelete?.id || deletingChatId) return;
    const chat = chatToDelete;

    try {
      setDeletingChatId(chat.id);
      const response = await axios.delete(
        `${REST_API}ai/sessions/${chat.id}`,
        sefiRequestConfig()
      );
      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Failed to delete chat');
      }

      const removeFromList = (sessions) => (sessions || []).filter((s) => s.id !== chat.id);

      setHistory((prev) => removeFromList(prev));
      setProjectSessionsMap((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = removeFromList(next[key]);
        });
        return next;
      });
      setSearchHistory((prev) => removeFromList(prev));

      if (currentChatId === chat.id) {
        forceNewSessionRef.current = true;
        currentChatIdRef.current = null;
        setCurrentChatId(null);
        clearLastChatSession();
        setMessages([]);
        setInput('');
        setSelectedFiles([]);
      }

      fetchProjects();
      setDeleteChatDialogOpen(false);
      setChatToDelete(null);
    } catch (err) {
      console.error('Failed to delete chat:', err);
    } finally {
      setDeletingChatId(null);
    }
  };

  const handleOpenNewProjectDialog = () => {
    setNewProjectName('');
    setProjectDialogOpen(true);
    setDrawerOpen(false);
  };

  const handleDeleteProject = (event, project) => {
    event.stopPropagation();
    if (!project?.id || deletingProjectId) return;
    setDeleteProjectError(null);
    setProjectToDelete(project);
    setDeleteProjectDialogOpen(true);
  };

  const handleCancelDeleteProject = () => {
    if (deletingProjectId) return;
    setDeleteProjectDialogOpen(false);
    setProjectToDelete(null);
    setDeleteProjectError(null);
  };

  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete?.id || deletingProjectId) return;
    const project = projectToDelete;
    const cacheKey = String(project.id);

    try {
      setDeletingProjectId(project.id);
      setDeleteProjectError(null);
      const response = await axios.delete(
        `${REST_API}ai/projects/${project.id}`,
        sefiRequestConfig()
      );
      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Failed to delete project');
      }

      const removedSessionIds = new Set(
        (projectSessionsMap[cacheKey] || [])
          .map((s) => s.id)
          .filter(Boolean)
      );

      const dropProjectSessions = (sessions) =>
        (sessions || []).filter((s) => {
          if (s.projectId === project.id) {
            if (s.id) removedSessionIds.add(s.id);
            return false;
          }
          return !removedSessionIds.has(s.id);
        });

      setProjects((prev) => (prev || []).filter((p) => p.id !== project.id));
      setProjectSessionsMap((prev) => {
        const next = { ...prev };
        delete next[cacheKey];
        return next;
      });
      setExpandedProjectKeys((prev) => {
        const next = { ...prev };
        delete next[cacheKey];
        return next;
      });
      setHistory((prev) => dropProjectSessions(prev));
      setSearchHistory((prev) => dropProjectSessions(prev));

      if (currentChatId && removedSessionIds.has(currentChatId)) {
        forceNewSessionRef.current = true;
        currentChatIdRef.current = null;
        setCurrentChatId(null);
        clearLastChatSession();
        setMessages([]);
        setInput('');
        setSelectedFiles([]);
      }

      setDeleteProjectDialogOpen(false);
      setProjectToDelete(null);

      if (selectedProjectId === project.id) {
        handleShowAllChats();
      }
    } catch (err) {
      console.error('Failed to delete project:', err);
      setDeleteProjectError(
        'The project could not be deleted. Please try again.'
      );
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name) return;
    try {
      setProjectSaving(true);
      const response = await axios.post(
        `${REST_API}ai/projects`,
        { name },
        sefiRequestConfig()
      );
      if (response?.data?.success && response.data.project) {
        const project = response.data.project;
        setProjects((prev) => [project, ...prev]);
        setProjectDialogOpen(false);
        setNewProjectName('');
        setSelectedProjectId(project.id);
        setExpandedProjectKeys((prev) => ({ ...prev, [String(project.id)]: true }));
        handleNewChat();
        fetchChatSessions(project.id);
      }
    } catch (err) {
      console.error('Failed to create project:', err);
    } finally {
      setProjectSaving(false);
    }
  };

  const filterSessionsByQuery = useCallback((sessions, query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((item) => (item.title || '').toLowerCase().includes(q));
  }, []);

  useEffect(() => {
    if (!searchDialogOpen) return undefined;
    let cancelled = false;
    const loadSearchHistory = async () => {
      try {
        setSearchHistoryLoading(true);
        const config = sefiRequestConfig();
        const response = await axios.get(`${REST_API}ai/sessions`, {
          headers: config.headers,
          params: { ...config.params, include_all: '1' },
        });
        if (!cancelled && response?.data?.success && Array.isArray(response.data.sessions)) {
          setSearchHistory(
            response.data.sessions.map((s) => ({
              id: s.id,
              title: s.title || 'New chat',
              projectId: s.project_id ?? null,
              messages: [],
            }))
          );
        }
      } catch (err) {
        console.error('Failed to load chats for search:', err);
      } finally {
        if (!cancelled) setSearchHistoryLoading(false);
      }
    };
    loadSearchHistory();
    return () => {
      cancelled = true;
    };
  }, [searchDialogOpen]);

  const searchVisibleSessions = useMemo(
    () => filterSessionsByQuery(searchHistory, searchModalQuery),
    [searchHistory, searchModalQuery, filterSessionsByQuery]
  );

  const renderSidebarSessionList = (sessions, loadingKey) => (
    <SidebarSessionList
      sessions={sessions}
      loadingKey={loadingKey}
      projectSessionsLoadingKey={projectSessionsLoadingKey}
      currentChatId={currentChatId}
      loadingChatId={loadingChatId}
      deletingChatId={deletingChatId}
      isDarkMode={isDarkMode}
      onLoadChat={handleLoadChat}
      onDeleteChat={handleDeleteChat}
    />
  );

  const projectNameById = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [projects]);

  useEffect(() => {
    if (open) {
      fetchProjects();
      fetchChatSessions(selectedProjectId);
      // Resume last working chat when panel reopens with no active session in memory
      // (e.g. after refresh). If state was kept from a prior open, leave it as-is.
      if (!currentChatIdRef.current) {
        const last = getLastChatSession();
        if (last?.sessionId) {
          handleLoadChat({ id: last.sessionId, projectId: last.projectId });
        }
      } else {
        forceNewSessionRef.current = false;
      }
    } else if (currentChatIdRef.current) {
      // Closing must not wipe the active chat — reopen should resume it.
      forceNewSessionRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleStopGeneration = useCallback(() => {
    const textAbort = sendAbortRef.current;
    const voiceAbort = voiceAbortRef.current;
    if (!textAbort && !voiceAbort) return;

    sendCancelledRef.current = true;
    if (textAbort) {
      textAbort.abort();
      sendAbortRef.current = null;
    }
    if (voiceAbort) {
      voiceAbort.abort();
      voiceAbortRef.current = null;
    }

    if (ackTimerRef.current) {
      clearTimeout(ackTimerRef.current);
      ackTimerRef.current = null;
    }
    audioPlayerRef.current?.stopAll();
    voiceActiveRef.current = false;

    const placeholderId = activePlaceholderRef.current;
    if (placeholderId != null) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? {
                id: placeholderId,
                role: 'ai',
                content: 'Generation stopped.',
                timestamp: new Date(),
              }
            : m
        )
      );
    }
    setSendMessage(false);
    activePlaceholderRef.current = null;

    if (conversationalModeRef.current) {
      setVoiceConversationState(VOICE_STATES.INTERRUPTED);
      setVoiceStatusDetail('Generation stopped — listening again…');
      scheduleNextListenRef.current?.();
    }
  }, []);

  const lastUserMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') return messages[i].id;
    }
    return null;
  }, [messages]);

  const handleEditLastPrompt = useCallback(
    (messageId) => {
      const msgIndex = messages.findIndex((m) => m.id === messageId);
      if (msgIndex < 0) return;

      const userMsg = messages[msgIndex];
      const priorMessages = messages.slice(0, msgIndex);

      setInput((userMsg.content || '').trim());
      setSelectedFiles(userMsg.files ? [...userMsg.files] : []);
      setMessages(priorMessages);

      if (currentChatId && !isTemporary) {
        setHistory((h) =>
          h.map((item) =>
            item.id === currentChatId ? { ...item, messages: priorMessages } : item
          )
        );
      }

      setTimeout(() => chatInputRef.current?.focus(), 0);
    },
    [messages, currentChatId, isTemporary]
  );

  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput && selectedFiles.length === 0) return;
    if (selectedFiles.some((f) => f.uploading)) return;

    const sendStartedAt = Date.now();

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: trimmedInput,
      files: selectedFiles,
      aspectRatio: selectedRatio,
      timestamp: new Date(),
    };

    const placeholderId = Date.now() + 1;
    const loadingPlaceholder = {
      id: placeholderId,
      role: 'ai',
      status: 'loading',
      currentStep: 'Understanding your question…',
      steps: [],
      content: '',
      timestamp: new Date(),
    };

    const filesToUpload = [...selectedFiles];
    const abortController = new AbortController();
    const { signal } = abortController;
    sendAbortRef.current = abortController;
    activePlaceholderRef.current = placeholderId;
    sendCancelledRef.current = false;

    const throwIfCancelled = () => {
      if (signal.aborted || sendCancelledRef.current) {
        throw new DOMException('Aborted', 'AbortError');
      }
    };

    try {
      setSendMessage(true);
      pinToBottom();
      setMessages((prev) => [...prev, userMessage, loadingPlaceholder]);
      setInput('');
      setSelectedImage(null);
      setSelectedFiles([]);

      const conversation_history = [...messages, userMessage]
        .slice(-10)
        .filter((m) => m.role !== 'system')
        .map((m) => ({
          role: m.role === 'ai' ? 'assistant' : 'user',
          content: m.content || '',
        }));

      const userId = getSefiUserId();
      const authHeaders = sefiAuthHeaders();
      const streamUrl = `${REST_API}ai/chat/stream`;

      let answerText = null;
      let ticketRows = [];
      let generatedTime = null;
      let sessionId = currentChatIdRef.current || currentChatId || null;
      let streamOk = true;
      let uploadResults = [];

      const ragFiles = filesToUpload.filter(
        (f) => f?.file && isRagDocumentFile(f) && !f.uploaded
      );
      const pendingVideos = filesToUpload.filter(
        (f) => f?.file && isVideoFile(f) && !f.uploaded
      );

      if (ragFiles.length > 0) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholderId
              ? {
                  ...m,
                  currentStep: 'Processing upload…',
                  steps: ['Processing upload…'],
                }
              : m
          )
        );
        const uploadResult = await uploadChatDocuments(ragFiles, {
          sessionId: currentChatIdRef.current || currentChatId || null,
          userId,
          newSession: forceNewSessionRef.current,
          signal,
          projectId: selectedProjectIdRef.current,
        });
        throwIfCancelled();
        uploadResults = uploadResult.results || [];
        if (uploadResult.sessionId) {
          sessionId = uploadResult.sessionId;
          if (!isTemporary) {
            currentChatIdRef.current = uploadResult.sessionId;
            setCurrentChatId(uploadResult.sessionId);
            forceNewSessionRef.current = false;
          }
        }
      }

      if (pendingVideos.length > 0) {
        const updateVideoProgress = (label) => {
          if (!label) return;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== placeholderId) return m;
              const steps = m.steps || [];
              const nextSteps = steps.includes(label) ? steps : [...steps, label];
              return {
                ...m,
                currentStep: label,
                steps: nextSteps,
              };
            })
          );
        };
        updateVideoProgress(VIDEO_PROCESSING_STEPS[0]);

        try {
          const videoUploadResult = await uploadChatVideos(pendingVideos, {
            sessionId: currentChatIdRef.current || currentChatId || null,
            userId,
            newSession: forceNewSessionRef.current,
            userQuestion: trimmedInput || null,
            onProgress: updateVideoProgress,
            signal,
            projectId: selectedProjectIdRef.current,
          });
          throwIfCancelled();
          uploadResults = [...uploadResults, ...(videoUploadResult.results || [])];
          if (videoUploadResult.sessionId) {
            sessionId = videoUploadResult.sessionId;
            if (!isTemporary) {
              currentChatIdRef.current = videoUploadResult.sessionId;
              setCurrentChatId(videoUploadResult.sessionId);
              forceNewSessionRef.current = false;
            }
          }
        } catch (videoErr) {
          if (signal.aborted || sendCancelledRef.current) {
            throw videoErr;
          }
          const videoErrMsg =
            videoErr?.response?.data?.error ||
            videoErr?.response?.data?.message ||
            videoErr?.message ||
            'Video processing failed. If the Python server restarted during upload, try again.';
          throw new Error(videoErrMsg);
        }
      }

      const blueprintData =
        filesToUpload.find((f) => f.blueprintData)?.blueprintData ||
        uploadResults.find((r) => isBlueprintUploadResult(r)) ||
        null;
      const videoData =
        filesToUpload.find((f) => f.videoData)?.videoData ||
        uploadResults.find((r) => isVideoUploadResult(r)) ||
        null;
      const hasDocumentRag =
        uploadResults.some(
          (r) =>
            (r.upload_type === 'document' || (r.document_id && !isBlueprintUploadResult(r))) &&
            !isVideoUploadResult(r)
        ) ||
        filesToUpload.some(
          (f) => f.uploaded && f.documentId && !f.blueprintId && !f.videoId
        );

      if (blueprintData && !hasDocumentRag) {
        answerText = '';
        streamOk = false;
      }

      if (videoData && !hasDocumentRag) {
        answerText =
          videoData.answer ||
          videoData.combined_summary ||
          videoData.visual_summary ||
          'Video processed.';
        streamOk = false;
      }

      const chatMessage =
        trimmedInput ||
        (hasDocumentRag
          ? filesToUpload.some((f) => isRagImageFile(f))
            ? 'What is written in this image?'
            : 'Summarize the uploaded document.'
          : videoData
            ? 'Summarize this uploaded video.'
          : blueprintData
            ? trimmedInput || 'What is this?'
            : '');

      if (!answerText) {
      try {
        const res = await fetch(streamUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          signal,
          body: JSON.stringify({
            message: chatMessage,
            conversation_history,
            session_id: sessionId || undefined,
            new_session: forceNewSessionRef.current,
            is_temporary: isTemporary,
            user_id: userId || undefined,
            ...(selectedProjectIdRef.current
              ? { project_id: selectedProjectIdRef.current }
              : {}),
          }),
        });

        if (!res.ok || !res.body) {
          streamOk = false;
        } else {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let sseBuffer = '';

          const updateProgress = (label) => {
            if (!label) return;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== placeholderId) return m;
                const steps = m.steps || [];
                const nextSteps = steps.includes(label) ? steps : [...steps, label];
                return {
                  ...m,
                  currentStep: label,
                  steps: nextSteps,
                };
              })
            );
          };

          const handleStreamEvents = (events) => {
            for (const ev of events) {
              if (ev.type === 'status' && ev.label) {
                updateProgress(ev.label);
              } else if (ev.type === 'done') {
                answerText = ev.answer || ev.ai_response;
                ticketRows = extractTicketRows(ev.sources);
                sessionId = ev.session_id ?? sessionId;
                if (sessionId && !isTemporary) {
                  currentChatIdRef.current = sessionId;
                  forceNewSessionRef.current = false;
                }
                if (ev.generated_time != null && ev.generated_time !== '') {
                  const t = Number(ev.generated_time);
                  if (Number.isFinite(t)) generatedTime = t;
                }
              } else if (ev.type === 'error') {
                answerText = ev.message || 'The AI service could not answer right now.';
                streamOk = false;
              }
            }
          };

          // eslint-disable-next-line no-constant-condition
          while (true) {
            throwIfCancelled();
            const { done, value } = await reader.read();
            if (value) {
              sseBuffer += decoder.decode(value, { stream: true });
              const { events, remainder } = parseSseFrames(sseBuffer);
              sseBuffer = remainder;
              handleStreamEvents(events);
            }
            if (done) break;
          }

          if (sseBuffer.trim()) {
            const { events } = parseSseFrames(`${sseBuffer}\n\n`);
            handleStreamEvents(events);
          }
        }
      } catch (streamErr) {
        if (signal.aborted || sendCancelledRef.current) {
          throw streamErr;
        }
        console.warn('SSE stream failed, falling back to /ai/chat:', streamErr);
        streamOk = false;
      }

      if (!streamOk || !answerText) {
        throwIfCancelled();
        const response = await axios.post(
          `${REST_API}ai/chat`,
          {
            message: chatMessage,
            conversation_history,
            session_id: sessionId || undefined,
            new_session: forceNewSessionRef.current,
            is_temporary: isTemporary,
            user_id: userId || undefined,
            ...(selectedProjectIdRef.current
              ? { project_id: selectedProjectIdRef.current }
              : {}),
          },
          { ...sefiRequestConfig(), signal }
        );
        answerText =
          response?.data?.answer ||
          response?.data?.ai_response ||
          (response?.data?.success === false
            ? 'The AI service could not answer right now. Check that the Python backend is running on port 8000.'
            : null);
        ticketRows = extractTicketRows(response?.data?.sources);
        sessionId = response?.data?.session_id;
        if (response?.data?.generated_time != null) {
          generatedTime = Number(response.data.generated_time);
        }
      }
      }

      throwIfCancelled();

      const wallSeconds = Math.round((Date.now() - sendStartedAt) / 10) / 100;
      const finalGeneratedTime = Number.isFinite(generatedTime)
        ? generatedTime
        : wallSeconds;

      const aiMessage = {
        id: placeholderId,
        role: 'ai',
        content: stripChatAsterisks(
          blueprintData
            ? ''
            : answerText ||
                `I've received your request about: "${trimmedInput}". How can I help you further?`
        ),
        blueprintResult: blueprintData || undefined,
        videoResult: videoData || undefined,
        tickets: ticketRows.length ? ticketRows : undefined,
        generatedTime: finalGeneratedTime,
        timestamp: new Date(),
      };
      setMessages((prev) => {
        const updated = prev.map((m) => (m.id === placeholderId ? aiMessage : m));
        if (sessionId && !isTemporary) {
          setHistory((h) =>
            h.map((item) =>
              item.id === sessionId ? { ...item, messages: updated } : item
            )
          );
        }
        return updated;
      });

      if (sessionId && !isTemporary) {
        forceNewSessionRef.current = false;
        currentChatIdRef.current = sessionId;
        setCurrentChatId(sessionId);
        await fetchChatSessions(selectedProjectIdRef.current);
        await fetchProjects();
      }
    } catch (error) {
      if (
        sendCancelledRef.current ||
        error?.name === 'AbortError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      setSendMessage(false);
      console.error('Error sending message:', error);
      const errMsg =
        error?.response?.data?.error ||
        error?.message ||
        "Sorry, I couldn't process your request right now. Please try again later.";
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? {
                id: placeholderId,
                role: 'ai',
                content: errMsg,
                timestamp: new Date(),
              }
            : m
        )
      );
    } finally {
      setInput('');
      setSelectedFiles([]);
      setSendMessage(false);
      sendAbortRef.current = null;
      activePlaceholderRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      setVoiceError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((track) => track.stop());
        await sendAudioToBackend(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting audio recording:', err);
      setVoiceError('Could not access microphone.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendAudioToBackend = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'voice_input.webm');

      const response = await axios.post(`${REST_API}ai/voice/transcribe`, formData, {
        headers: {
          ...sefiAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.success) {
        setInput(response.data.text || '');
      } else {
        setVoiceError(response.data.message || 'Could not transcribe the voice input.');
      }
    } catch (err) {
      console.error('Error sending audio to backend:', err);
      setVoiceError('Voice transcription failed.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleMicClick = () => {
    if (!hasSeenVoiceOnboarding) {
      setShowVoiceOnboarding(true);
    } else {
      if (isRecording) {
        stopRecording();
      } else {
        startRecording();
      }
    }
  };

  const handleContinueVoice = () => {
    setHasSeenVoiceOnboarding(true);
    setShowVoiceOnboarding(false);
    localStorage.setItem('hasSeenVoiceOnboarding', 'true');
    startRecording();
  };

  const clearVoiceAckTimer = () => {
    if (ackTimerRef.current) {
      clearTimeout(ackTimerRef.current);
      ackTimerRef.current = null;
    }
  };

  const stopVoicePlayback = () => {
    clearVoiceAckTimer();
    audioPlayerRef.current?.stopAll();
    if (voiceAbortRef.current) {
      voiceAbortRef.current.abort();
      voiceAbortRef.current = null;
    }
  };

  const clearVadMonitoring = () => {
    if (vadSendTimerRef.current) {
      clearInterval(vadSendTimerRef.current);
      vadSendTimerRef.current = null;
    }
    if (maxRecordTimerRef.current) {
      clearTimeout(maxRecordTimerRef.current);
      maxRecordTimerRef.current = null;
    }
    if (pcmCaptureRef.current) {
      pcmCaptureRef.current.stop();
      pcmCaptureRef.current = null;
    }
    pcmPendingRef.current = [];
    const sessionId = vadSessionIdRef.current;
    vadSessionIdRef.current = null;
    if (sessionId) {
      axios
        .delete(`${REST_API}ai/voice/vad/session/${encodeURIComponent(sessionId)}`, sefiRequestConfig())
        .catch(() => {});
    }
  };

  const flushPcmToVad = async () => {
    const sessionId = vadSessionIdRef.current;
    if (!sessionId || vadAutoStopRef.current || pcmPendingRef.current.length === 0) return;

    const parts = pcmPendingRef.current.splice(0, pcmPendingRef.current.length);
    const totalLen = parts.reduce((sum, p) => sum + p.byteLength, 0);
    const merged = new Uint8Array(totalLen);
    let offset = 0;
    parts.forEach((buf) => {
      merged.set(new Uint8Array(buf), offset);
      offset += buf.byteLength;
    });

    try {
      const response = await axios.post(
        `${REST_API}ai/voice/vad/chunk`,
        {
          session_id: sessionId,
          sample_rate: pcmCaptureRef.current?.getSampleRate?.() || 48000,
          pcm_base64: pcmToBase64(merged.buffer),
          reset: false,
        },
        sefiRequestConfig()
      );
      const event = response.data?.event;
      if (event === 'speech_start') {
        speechDetectedRef.current = true;
        setVoiceStatusDetail('Listening… (speech detected)');
      }
      // Only auto-stop after a real speech_start — never invent speech on speech_end alone.
      if (event === 'speech_end' && !vadAutoStopRef.current) {
        if (!speechDetectedRef.current) {
          return;
        }
        vadAutoStopRef.current = true;
        setVoiceStatusDetail('Speech ended — processing…');
        flushPcmToVad().finally(() => stopConversationRecording());
      }
    } catch (err) {
      console.warn('VAD chunk check failed:', err?.message || err);
    }
  };

  const stopConversationRecording = () => {
    clearVadMonitoring();
    const recorder = conversationRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    if (conversationStreamRef.current) {
      conversationStreamRef.current.getTracks().forEach((t) => t.stop());
      conversationStreamRef.current = null;
    }
  };

  const scheduleNextListen = () => {
    if (!conversationalModeRef.current) return;
    if (scheduleListenTimerRef.current) {
      clearTimeout(scheduleListenTimerRef.current);
    }
    scheduleListenTimerRef.current = setTimeout(() => {
      scheduleListenTimerRef.current = null;
      if (!conversationalModeRef.current) return;
      const recorder = conversationRecorderRef.current;
      if (recorder && recorder.state === 'recording') return;
      startConversationRecordingRef.current?.();
    }, 450);
  };
  scheduleNextListenRef.current = scheduleNextListen;

  const exitConversationalMode = () => {
    conversationalModeRef.current = false;
    setIsConversationalMode(false);
    if (scheduleListenTimerRef.current) {
      clearTimeout(scheduleListenTimerRef.current);
      scheduleListenTimerRef.current = null;
    }
    vadAutoStopRef.current = true;
    voiceActiveRef.current = false;
    stopVoicePlayback();
    stopConversationRecording();
    clearVadMonitoring();
    setVoiceTranscript('');
    setVoiceError(null);
    setVoiceConversationState(VOICE_STATES.IDLE);
    setVoiceStatusDetail('');
  };
  exitConversationalModeRef.current = exitConversationalMode;

  const enterConversationalMode = () => {
    conversationalModeRef.current = true;
    setIsConversationalMode(true);
    setVoiceError(null);
    vadAutoStopRef.current = false;
    startConversationRecordingRef.current?.();
  };

  const handleChatClose = (e) => {
    exitConversationalMode();
    onClose(e);
  };

  const transcribeConversationAudio = async (blob) => {
    setVoiceConversationState(VOICE_STATES.TRANSCRIBING);
    setVoiceStatusDetail('Transcribing your speech…');
    const formData = new FormData();
    formData.append('file', blob, 'voice_conversation.webm');
    const response = await axios.post(`${REST_API}ai/voice/transcribe`, formData, {
      headers: {
        ...sefiAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    if (!response.data?.success || !String(response.data.text || '').trim()) {
      throw new Error(response.data?.message || response.data?.error || 'Could not transcribe speech.');
    }
    return {
      text: String(response.data.text).trim(),
      language: response.data.language || null,
    };
  };

  const playVoiceAnswer = async (answerText) => {
    if (!answerText?.trim()) return;
    try {
      const synthRes = await axios.post(
        `${REST_API}ai/voice/synthesize`,
        { text: answerText },
        sefiRequestConfig()
      );
      if (!synthRes.data?.success || !synthRes.data?.chunks?.length) {
        voiceActiveRef.current = false;
        if (conversationalModeRef.current) {
          setVoiceStatusDetail('Could not play audio — listening again…');
          scheduleNextListen();
        } else {
          setVoiceConversationState(VOICE_STATES.IDLE);
          setVoiceStatusDetail('');
        }
        return;
      }
      voiceActiveRef.current = true;
      audioPlayerRef.current?.resetStopped();
      setVoiceConversationState(VOICE_STATES.SPEAKING);
      setVoiceStatusDetail('Speaking…');
      synthRes.data.chunks.forEach((chunk) => {
        if (chunk?.audio_base64) {
          audioPlayerRef.current?.enqueueBase64Wav(chunk.audio_base64, chunk.mime_type || 'audio/wav');
        }
      });
    } catch (err) {
      console.warn('Voice synthesis failed — text answer still shown:', err);
      voiceActiveRef.current = false;
      if (conversationalModeRef.current) {
        setVoiceStatusDetail('Could not play audio — listening again…');
        scheduleNextListen();
      } else {
        setVoiceConversationState(VOICE_STATES.IDLE);
        setVoiceStatusDetail('');
      }
    }
  };

  const submitVoiceTranscript = async (transcript, language) => {
    const trimmedInput = transcript.trim();
    if (!trimmedInput) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: trimmedInput,
      voiceLanguage: language || undefined,
      timestamp: new Date(),
    };

    const placeholderId = Date.now() + 1;
    const loadingPlaceholder = {
      id: placeholderId,
      role: 'ai',
      status: 'loading',
      currentStep: 'Understanding your request…',
      steps: [],
      content: '',
      timestamp: new Date(),
    };

    const abortController = new AbortController();
    voiceAbortRef.current = abortController;
    const { signal } = abortController;
    ackPlayedRef.current = false;
    clearVoiceAckTimer();

    setVoiceConversationState(VOICE_STATES.THINKING);
    setVoiceStatusDetail('Understanding your request…');
    setSendMessage(true);
    activePlaceholderRef.current = placeholderId;
    pinToBottom();
    setMessages((prev) => [...prev, userMessage, loadingPlaceholder]);

    ackTimerRef.current = setTimeout(async () => {
      if (ackPlayedRef.current || signal.aborted) return;
      ackPlayedRef.current = true;
      try {
        const ackRes = await axios.get(`${REST_API}ai/voice/acknowledgement`, sefiRequestConfig());
        if (ackRes.data?.success && ackRes.data?.audio_base64 && !signal.aborted) {
          audioPlayerRef.current?.stopCurrent();
          await audioPlayerRef.current?.playBase64WavNow(
            ackRes.data.audio_base64,
            ackRes.data.mime_type || 'audio/wav'
          );
        }
      } catch (_) {
        /* optional acknowledgement */
      }
    }, 1800);

    const conversation_history = [...messages, userMessage]
      .slice(-10)
      .filter((m) => m.role !== 'system')
      .map((m) => ({
        role: m.role === 'ai' ? 'assistant' : 'user',
        content: m.content || '',
      }));

    const userId = getSefiUserId();
    const authHeaders = sefiAuthHeaders();
    let answerText = null;
    let ticketRows = [];
    let generatedTime = null;
    let sessionId = currentChatIdRef.current || currentChatId || null;
    let streamOk = true;

    try {
      const res = await fetch(`${REST_API}ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        signal,
        body: JSON.stringify({
          message: trimmedInput,
          conversation_history,
          session_id: sessionId || undefined,
          new_session: forceNewSessionRef.current,
          is_temporary: isTemporary,
          user_id: userId || undefined,
          ...(selectedProjectIdRef.current ? { project_id: selectedProjectIdRef.current } : {}),
        }),
      });

      if (!res.ok || !res.body) {
        streamOk = false;
      } else {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let sseBuffer = '';

        const updateProgress = (label) => {
          if (!label || signal.aborted) return;
          setVoiceStatusDetail(label);
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== placeholderId) return m;
              const steps = m.steps || [];
              const nextSteps = steps.includes(label) ? steps : [...steps, label];
              return { ...m, currentStep: label, steps: nextSteps };
            })
          );
        };

        const handleStreamEvents = (events) => {
          for (const ev of events) {
            if (ev.type === 'status' && ev.label) updateProgress(ev.label);
            else if (ev.type === 'done') {
              answerText = ev.answer || ev.ai_response;
              ticketRows = extractTicketRows(ev.sources);
              sessionId = ev.session_id ?? sessionId;
              if (sessionId && !isTemporary) {
                currentChatIdRef.current = sessionId;
                forceNewSessionRef.current = false;
              }
              if (ev.generated_time != null && ev.generated_time !== '') {
                const t = Number(ev.generated_time);
                if (Number.isFinite(t)) generatedTime = t;
              }
            } else if (ev.type === 'error') {
              answerText = ev.message || 'The AI service could not answer right now.';
              streamOk = false;
            }
          }
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (signal.aborted) break;
          const { done, value } = await reader.read();
          if (value) {
            sseBuffer += decoder.decode(value, { stream: true });
            const { events, remainder } = parseSseFrames(sseBuffer);
            sseBuffer = remainder;
            handleStreamEvents(events);
          }
          if (done) break;
        }
        if (sseBuffer.trim()) {
          const { events } = parseSseFrames(`${sseBuffer}\n\n`);
          handleStreamEvents(events);
        }
      }

      if (!streamOk || !answerText) {
        const response = await axios.post(
          `${REST_API}ai/chat`,
          {
            message: trimmedInput,
            conversation_history,
            session_id: sessionId || undefined,
            new_session: forceNewSessionRef.current,
            is_temporary: isTemporary,
            user_id: userId || undefined,
            ...(selectedProjectIdRef.current ? { project_id: selectedProjectIdRef.current } : {}),
          },
          { ...sefiRequestConfig(), signal }
        );
        answerText =
          response?.data?.answer ||
          response?.data?.ai_response ||
          'The AI service could not answer right now.';
        ticketRows = extractTicketRows(response?.data?.sources);
        sessionId = response?.data?.session_id;
        if (response?.data?.generated_time != null) {
          generatedTime = Number(response.data.generated_time);
        }
      }

      clearVoiceAckTimer();
      audioPlayerRef.current?.stopCurrent();

      const finalGeneratedTime = Number.isFinite(generatedTime)
        ? generatedTime
        : Math.round((Date.now() - userMessage.id) / 10) / 100;

      const aiMessage = {
        id: placeholderId,
        role: 'ai',
        content: stripChatAsterisks(answerText || 'No answer returned.'),
        tickets: ticketRows.length ? ticketRows : undefined,
        generatedTime: finalGeneratedTime,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const updated = prev.map((m) => (m.id === placeholderId ? aiMessage : m));
        if (sessionId && !isTemporary) {
          setHistory((h) =>
            h.map((item) => (item.id === sessionId ? { ...item, messages: updated } : item))
          );
        }
        return updated;
      });

      if (sessionId && !isTemporary) {
        forceNewSessionRef.current = false;
        currentChatIdRef.current = sessionId;
        setCurrentChatId(sessionId);
        await fetchChatSessions(selectedProjectIdRef.current);
        await fetchProjects();
      }

      if (!signal.aborted) {
        await playVoiceAnswer(aiMessage.content);
      }
    } catch (error) {
      clearVoiceAckTimer();
      audioPlayerRef.current?.stopCurrent();
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholderId && m.status === 'loading'
              ? {
                  id: placeholderId,
                  role: 'ai',
                  content: 'Generation stopped.',
                  timestamp: new Date(),
                }
              : m
          )
        );
        if (conversationalModeRef.current) {
          setVoiceStatusDetail('Generation stopped — listening again…');
          scheduleNextListen();
        } else {
          setVoiceConversationState(VOICE_STATES.INTERRUPTED);
          setVoiceStatusDetail('Interrupted');
        }
        return;
      }
      const errMsg =
        error?.response?.data?.error ||
        error?.message ||
        "Sorry, I couldn't process your voice request right now.";
      setVoiceError(errMsg);
      setVoiceConversationState(VOICE_STATES.ERROR);
      setVoiceStatusDetail(errMsg);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? { id: placeholderId, role: 'ai', content: errMsg, timestamp: new Date() }
            : m
        )
      );
      if (conversationalModeRef.current) {
        setTimeout(() => {
          if (!conversationalModeRef.current) return;
          setVoiceError(null);
          scheduleNextListen();
        }, 2500);
      }
    } finally {
      setSendMessage(false);
      voiceAbortRef.current = null;
      if (activePlaceholderRef.current === placeholderId) {
        activePlaceholderRef.current = null;
      }
      if (!voiceActiveRef.current && !conversationalModeRef.current) {
        setVoiceConversationState(VOICE_STATES.IDLE);
        setVoiceStatusDetail('');
      }
    }
  };

  const processConversationRecording = async (blob) => {
    // No real speech detected — stay idle and keep listening (do not invent a question).
    if (!speechDetectedRef.current) {
      if (conversationalModeRef.current) {
        setVoiceStatusDetail('Listening for your next question…');
        scheduleNextListen();
      } else {
        setVoiceConversationState(VOICE_STATES.IDLE);
        setVoiceStatusDetail('');
      }
      return;
    }
    try {
      const { text, language } = await transcribeConversationAudio(blob);
      const meaningful = String(text || '').replace(/[^a-zA-Z0-9]/g, '');
      if (!meaningful) {
        if (conversationalModeRef.current) {
          setVoiceStatusDetail('Listening for your next question…');
          scheduleNextListen();
        }
        return;
      }
      setVoiceTranscript(text);
      setVoiceStatusDetail('Understanding your request…');
      await submitVoiceTranscript(text, language);
    } catch (err) {
      console.error('Voice conversation failed:', err);
      const errMsg = err?.response?.data?.error || err?.message || 'Voice transcription failed.';
      const isNoSpeech = /no speech|could not transcribe|empty/i.test(String(errMsg));
      if (conversationalModeRef.current && isNoSpeech) {
        setVoiceError(null);
        setVoiceStatusDetail('Listening for your next question…');
        scheduleNextListen();
        return;
      }
      setVoiceError(errMsg);
      setVoiceConversationState(VOICE_STATES.ERROR);
      setVoiceStatusDetail(errMsg);
      if (conversationalModeRef.current) {
        setTimeout(() => {
          if (!conversationalModeRef.current) return;
          setVoiceError(null);
          scheduleNextListen();
        }, 2500);
      }
    }
  };

  const startConversationRecording = async () => {
    if (!conversationalModeRef.current) return;
    try {
      setVoiceError(null);
      setVoiceTranscript('');
      voiceActiveRef.current = false;
      vadAutoStopRef.current = false;
      speechDetectedRef.current = false;
      vadSessionIdRef.current = `vad_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      conversationStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      conversationRecorderRef.current = mediaRecorder;
      conversationChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data?.size > 0) conversationChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        clearVadMonitoring();
        const audioBlob = new Blob(conversationChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((track) => track.stop());
        conversationStreamRef.current = null;
        if (audioBlob.size > 0) {
          setVoiceConversationState(VOICE_STATES.TRANSCRIBING);
          await processConversationRecording(audioBlob);
        } else if (conversationalModeRef.current) {
          setVoiceStatusDetail('Listening for your next question…');
          scheduleNextListen();
        } else {
          setVoiceConversationState(VOICE_STATES.IDLE);
          setVoiceStatusDetail('');
        }
      };

      pcmCaptureRef.current = startPcmCapture(stream, {
        onPcmChunk: (pcmBuffer) => {
          if (vadAutoStopRef.current) return;
          pcmPendingRef.current.push(pcmBuffer);
        },
      });

      vadSendTimerRef.current = setInterval(() => {
        flushPcmToVad();
      }, 350);

      maxRecordTimerRef.current = setTimeout(() => {
        if (!vadAutoStopRef.current && conversationRecorderRef.current?.state === 'recording') {
          vadAutoStopRef.current = true;
          // Timeout with no speech → idle listen again; do not invent a question.
          if (!speechDetectedRef.current) {
            setVoiceStatusDetail('Listening for your next question…');
            stopConversationRecording();
            return;
          }
          setVoiceStatusDetail('Maximum recording time reached…');
          stopConversationRecording();
        }
      }, 30000);

      mediaRecorder.start(500);
      setVoiceConversationState(VOICE_STATES.LISTENING);
      setVoiceStatusDetail(
        conversationalModeRef.current ? 'Voice conversation active — speak now' : 'Listening… speak now'
      );
    } catch (err) {
      clearVadMonitoring();
      console.error('Conversation recording error:', err);
      setVoiceError('Could not access microphone.');
      setVoiceConversationState(VOICE_STATES.ERROR);
      setVoiceStatusDetail('Microphone permission denied.');
      if (conversationalModeRef.current) {
        conversationalModeRef.current = false;
        setIsConversationalMode(false);
      }
    }
  };
  startConversationRecordingRef.current = startConversationRecording;

  const handleWaveformClick = () => {
    if (!conversationalModeRef.current) {
      enterConversationalMode();
      return;
    }

    const isSpeaking =
      voiceConversationState === VOICE_STATES.SPEAKING || audioPlayerRef.current?.isPlaying();
    if (isSpeaking) {
      stopVoicePlayback();
      voiceActiveRef.current = false;
      setVoiceStatusDetail('Interrupted — listening…');
      scheduleNextListen();
    }
  };

  const handleExitConversationalModeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    exitConversationalMode();
  };

  const renderConversationalCloseButton = ({ size = 34 } = {}) => {
    if (!isConversationalMode) return null;
    return (
      <Tooltip title="Close voice conversation" arrow placement="top">
        <IconButton
          size="small"
          onClick={handleExitConversationalModeClick}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Close voice conversation"
          sx={{
            position: 'absolute',
            right: maximized ? 10 : 8,
            bottom: maximized ? 10 : 12,
            zIndex: 5,
            width: size,
            height: size,
            minWidth: size,
            p: 0,
            bgcolor: '#ffffff',
            color: '#1a1a1a',
            border: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
            '&:hover': {
              bgcolor: '#f2f2f2',
            },
            '&:active': {
              bgcolor: '#e6e6e6',
              transform: 'scale(0.96)',
            },
            '&:focus-visible': {
              outline: '2px solid #10a37f',
              outlineOffset: 2,
              bgcolor: '#ffffff',
            },
            '&.Mui-disabled': {
              bgcolor: '#f5f5f5',
              color: 'rgba(0,0,0,0.28)',
              opacity: 1,
            },
          }}
        >
          <Close sx={{ fontSize: size >= 36 ? 18 : 16, color: 'inherit' }} />
        </IconButton>
      </Tooltip>
    );
  };

  const renderVoiceWaveformControl = ({ waveSize = 22, btnSize = 34 }) => (
    <Tooltip
      title={
        isConversationalMode
          ? voiceConversationState === VOICE_STATES.SPEAKING
            ? 'Interrupt and listen again'
            : 'Voice conversation active'
          : 'Start voice conversation'
      }
      arrow
    >
      <IconButton
        onClick={handleWaveformClick}
        disabled={isTranscribing && voiceConversationState !== VOICE_STATES.LISTENING}
        size="small"
        sx={{
          bgcolor: isConversationalMode ? '#0d7a5f' : '#000',
          color: '#fff',
          width: btnSize,
          height: btnSize,
          opacity: 1,
          boxShadow: isConversationalMode ? '0 0 0 2px rgba(76, 175, 80, 0.55)' : 'none',
          animation:
            isConversationalMode &&
            (voiceConversationState === VOICE_STATES.LISTENING ||
              voiceConversationState === VOICE_STATES.IDLE)
              ? 'voicePulse 1.5s infinite'
              : 'none',
          '@keyframes voicePulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
            '70%': { boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
          },
          '&:hover': { bgcolor: isConversationalMode ? '#0a6b52' : '#222' },
        }}
      >
        <VoiceWaveIcon size={waveSize} color="white" />
      </IconButton>
    </Tooltip>
  );

  const handleTemporaryToggle = () => {
    if (!isTemporary && !hasSeenTemporaryOnboarding) {
      setShowTemporaryOnboarding(true);
    } else {
      setIsTemporary(!isTemporary);
    }
  };

  const handleContinueTemporary = () => {
    setHasSeenTemporaryOnboarding(true);
    setShowTemporaryOnboarding(false);
    localStorage.setItem('hasSeenTemporaryOnboarding', 'true');
    setIsTemporary(true);
  };

  const handleToggleMaximize = () => {
    setMaximized(!maximized);
  };

  // Maximizing changes layout height / message padding — jump to latest so chat isn't stuck at the top.
  useEffect(() => {
    if (!open || messages.length === 0) return;
    pinToBottom('auto');
    // Sidebar / flex layout can settle after the first paint.
    const t = setTimeout(() => pinToBottom('auto'), 280);
    return () => clearTimeout(t);
  }, [maximized]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderMessage = (msg) => (
    <ChatMessage
      key={msg.id}
      msg={msg}
      theme={theme}
      maximized={maximized}
      isDarkMode={isDarkMode}
      lastUserMessageId={lastUserMessageId}
      sendMessage={sendMessage}
      onPreviewImage={setPreviewImage}
      onEditLastPrompt={handleEditLastPrompt}
      userProfilePic={user?.profile_pic}
      userName={user?.person_name}
    />
  );

  const SidebarContent = (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: isDarkMode ? CHAT_GPT_LIGHT_DARK : '#ffffff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          variant="text"
          startIcon={<Edit fontSize="small" />}
          fullWidth
          onClick={handleNewChat}
          sx={{
            justifyContent: 'flex-start',
            color: isDarkMode ? 'white' : 'black',
            borderRadius: '10px',
            py: 1,
            px: 1.5,
            textTransform: 'none',
            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
          }}
        >
          New chat
        </Button>
        {maximized && (
          <Tooltip title="Close sidebar" arrow>
            <IconButton 
              size="small" 
              onClick={() => setDesktopSidebarOpen(false)} 
              sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'gray', p: 1, borderRadius: '8px' }}
            >
              <Box sx={{ 
                width: 18, 
                height: 18, 
                border: `2px solid currentColor`, 
                borderRadius: '3px',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 5,
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  bgcolor: 'currentColor'
                }
              }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Button
        variant="text"
        startIcon={<CreateNewFolder fontSize="small" sx={{ opacity: 0.85 }} />}
        fullWidth
        onClick={handleOpenNewProjectDialog}
        sx={{
          justifyContent: 'flex-start',
          color: isDarkMode ? 'white' : 'black',
          mb: 0.5,
          borderRadius: '10px',
          py: 1,
          px: 1.5,
          textTransform: 'none',
          fontWeight: 400,
          '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' },
        }}
      >
        New folder
      </Button>

      <Button
        variant="text"
        startIcon={<Search fontSize="small" sx={{ opacity: 0.7 }} />}
        fullWidth
        onClick={() => setSearchDialogOpen(true)}
        sx={{
          justifyContent: 'flex-start',
          color: isDarkMode ? 'white' : 'black',
          mb: 1,
          borderRadius: '10px',
          py: 1,
          px: 1.5,
          textTransform: 'none',
          fontWeight: 400,
          '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
        }}
      >
        Search chats
      </Button>

      <Stack spacing={0.5} sx={{ flex: 1, overflowY: 'auto', mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'gray', px: 1, fontWeight: 600 }}>
          Folders
        </Typography>
        {projectsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        {!projectsLoading &&
          projects.map((project) => {
            const cacheKey = String(project.id);
            const isExpanded = !!expandedProjectKeys[cacheKey];
            return (
              <Box key={project.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '10px',
                    bgcolor:
                      selectedProjectId === project.id
                        ? isDarkMode
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(0,0,0,0.04)'
                        : 'transparent',
                    '&:hover': {
                      bgcolor: isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => toggleProjectExpanded(project.id)}
                    sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'gray', p: 0.75 }}
                  >
                    <KeyboardArrowDown
                      sx={{
                        fontSize: 18,
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                  <Box
                    onClick={() => handleSelectProject(project.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flex: 1,
                      py: 0.9,
                      pr: 0.5,
                      cursor: 'pointer',
                      color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                      minWidth: 0,
                    }}
                  >
                    <FolderOpen
                      sx={{
                        fontSize: 17,
                        color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                        flexShrink: 0,
                        ml: 0.5,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                        ml: 0.5,
                      }}
                    >
                      {project.name}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        gap: 0.15,
                      }}
                    >
                      <Tooltip title="New chat in project" arrow>
                        <IconButton
                          size="small"
                          aria-label={`Create new chat in ${project.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNewChatInProject(project.id);
                          }}
                          sx={{
                            p: 0.5,
                            color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'gray',
                            '&:hover': {
                              color: isDarkMode ? '#fff' : '#111',
                              bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.06)'
                                : 'rgba(0,0,0,0.04)',
                            },
                          }}
                        >
                          <AddComment sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete project" arrow>
                        <IconButton
                          className="project-delete-btn"
                          size="small"
                          aria-label={`Delete ${project.name} project`}
                          onClick={(e) => handleDeleteProject(e, project)}
                          disabled={deletingProjectId === project.id}
                          sx={{
                            p: 0.5,
                            transition: 'color 0.15s',
                            color: isDarkMode
                              ? 'rgba(255,255,255,0.45)'
                              : 'rgba(0,0,0,0.35)',
                            '&:hover': {
                              color: '#e57373',
                              bgcolor: 'transparent',
                            },
                          }}
                        >
                          {deletingProjectId === project.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteOutline sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
                <Collapse in={isExpanded}>
                  {renderSidebarSessionList(projectSessionsMap[cacheKey], cacheKey)}
                </Collapse>
              </Box>
            );
          })}
        {!projectsLoading && projects.length === 0 && (
          <Typography sx={{ color: 'gray', px: 1.5, py: 0.5, fontSize: '0.78rem' }}>
            No projects yet
          </Typography>
        )}
        {!projectsLoading && (
          <Box sx={{ mt: 0.5 }}>
            <Box
              onClick={handleShowAllChats}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: 1.5,
                pr: 1.5,
                py: 0.9,
                borderRadius: '10px',
                cursor: 'pointer',
                color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                bgcolor:
                  selectedProjectId == null
                    ? isDarkMode
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.04)'
                    : 'transparent',
                '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <Forum sx={{ fontSize: 17, color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)' }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>All chats</Typography>
            </Box>
            {selectedProjectId == null &&
              renderSidebarSessionList(projectSessionsMap.all ?? history, 'all')}
          </Box>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <ListItem button sx={{ borderRadius: '8px' }}>
        <ListItemIcon sx={{ minWidth: 12 }}>
          {user?.profile_pic ? (
            <Avatar src={user.profile_pic} alt={user.person_name} sx={{ width: 28, height: 28 }} />
          ) : (
            <Avatar sx={{ width: 28, height: 28 }}>{user?.person_name?.charAt(0)}</Avatar>
          )}
        </ListItemIcon>
        <ListItemText 
          primary={user?.person_name || 'User'} 
          secondary={user?.email || ''}
          primaryTypographyProps={{ variant: 'body2', color: isDarkMode ? 'white' : 'black', noWrap: true }} 
          secondaryTypographyProps={{ variant: 'caption', color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)', noWrap: true }}
        />
      </ListItem>
    </Box>
  );

  const SidebarSlim = (
    <Box sx={{ 
      width: 60, 
      height: '100%', 
      display: { xs: 'none', md: 'flex' }, 
      flexDirection: 'column', 
      alignItems: 'center', 
      py: 2,
      gap: 2,
      bgcolor: isDarkMode ? CHAT_GPT_LIGHT_DARK : '#ffffff',
      borderRight: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
      zIndex: 10
    }}>
      <Tooltip title="Open sidebar" arrow placement="right">
        <IconButton 
          size="small" 
          onClick={() => setDesktopSidebarOpen(true)} 
          sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'gray', p: 1, borderRadius: '8px' }}
        >
          <Box sx={{ 
            width: 18, 
            height: 18, 
            border: `2px solid currentColor`, 
            borderRadius: '3px',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 5,
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: 'currentColor'
            }
          }} />
        </IconButton>
      </Tooltip>


      <Tooltip title="New chat" arrow placement="right">
        <IconButton size="small" onClick={handleNewChat} sx={{ color: isDarkMode ? 'white' : 'black', p: 1.2, '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Search chats" arrow placement="right">
        <IconButton size="small" onClick={() => setSearchDialogOpen(true)} sx={{ color: isDarkMode ? 'white' : 'black', p: 1.2, '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
          <Search fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Recents" arrow placement="right">
        <IconButton 
          size="small" 
          onClick={(e) => setRecentsAnchor(e.currentTarget)} 
          sx={{ 
            color: isDarkMode ? 'white' : 'black', 
            p: 1.2, 
            bgcolor: Boolean(recentsAnchor) ? (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } 
          }}
        >
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={recentsAnchor}
        open={Boolean(recentsAnchor)}
        onClose={() => setRecentsAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{
          sx: {
            ml: 1,
            mt: 0,
            borderRadius: '16px',
            bgcolor: isDarkMode ? '#2f2f2f' : '#ffffff',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
            boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
            width: 280,
            maxHeight: 400,
            overflowY: 'auto',
            backgroundImage: 'none',
            p: 1,
            '& .MuiList-root': { p: 0 }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'gray', fontWeight: 600, display: 'block', mb: 1 }}>Recents</Typography>
          <Stack spacing={0.5}>
            {historyLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!historyLoading && history.map((item) => (
              <Box
                key={item.id}
                onClick={() => {
                  handleLoadChat(item);
                  setRecentsAnchor(null);
                }}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                  bgcolor: currentChatId === item.id ? (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
                  '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.title}
                </Typography>
              </Box>
            ))}
            {!historyLoading && history.length === 0 && (
              <Typography sx={{ color: 'gray', fontSize: '0.8rem', px: 1 }}>No chats yet</Typography>
            )}
          </Stack>
        </Box>
      </Menu>

      <Box sx={{ flex: 1 }} />

      <Divider sx={{ width: '60%', my: 1 }} />
    </Box>
  );

  const MobileHeader = (
    <Box sx={{
      p: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: isDarkMode ? CHAT_GPT_LIGHT_DARK : '#ffffff',
      position: 'relative',
      borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
      transition: 'background-color 0.3s'
    }}>
      {/* <Tooltip title="Main Menu" arrow> */}
      <IconButton size="small" onClick={() => setDrawerOpen(!drawerOpen)} sx={{ p: 1, position: 'absolute', left: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
          <Box sx={{ width: 18, height: 2, bgcolor: isDarkMode ? '#fff' : '#000', borderRadius: 1 }} />
          <Box sx={{ width: 12, height: 2, bgcolor: isDarkMode ? '#fff' : '#000', borderRadius: 1 }} />
        </Box>
      </IconButton>
      {/* </Tooltip> */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box sx={{ width: 24, height: 24 }}><Lottie animationData={robotIcon} loop={true} /></Box>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDarkMode ? 'white' : 'black' }}>SEFI</Typography>
        {/* <KeyboardArrowDown fontSize="small" sx={{ color: isDarkMode ? 'white' : 'black' }} /> */}
      </Stack>
      <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={isDarkMode ? "Light Mode" : "Dark Mode"} arrow>
          <IconButton size="small" onClick={() => setIsDarkMode(!isDarkMode)} sx={{ color: isDarkMode ? 'white' : 'gray' }}>
            {isDarkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
          </IconButton>
        </Tooltip>
        {messages.length === 0 && (
          <Tooltip title={isTemporary ? "Turn off temporary chat" : "Turn on temporary chat"} arrow>
            <IconButton 
              size="small" 
              onClick={handleTemporaryToggle}
              sx={{ 
                color: isDarkMode ? 'white' : 'gray',
                bgcolor: 'transparent'
              }}
            >
              <TemporaryChatIcon size={18} active={isTemporary} />
            </IconButton>
          </Tooltip>
        )}
        {!maximized && (
          <Tooltip title="Maximize" arrow>
            <IconButton
              size="small"
              onClick={handleToggleMaximize}
              sx={{ color: 'gray' }}
            >
              <Fullscreen fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );


  const DesktopHeader = (
    <Box sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      bgcolor: isDarkMode ? CHAT_GPT_LIGHT_DARK : '#ffffff',
      borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
      transition: 'background-color 0.3s'
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 30, height: 30 }}><Lottie animationData={robotIcon} loop={true} /></Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isDarkMode ? 'white' : 'black' }}>SEFI</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Tooltip title={isDarkMode ? "Light Mode" : "Dark Mode"} arrow>
          <IconButton size="small" onClick={() => setIsDarkMode(!isDarkMode)} sx={{ color: isDarkMode ? 'white' : 'gray' }}>
            {isDarkMode ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Tooltip>
        {messages.length === 0 && (
          <Tooltip title={isTemporary ? "Turn off temporary chat" : "Turn on temporary chat"} arrow>
            <IconButton 
              size="small" 
              onClick={handleTemporaryToggle}
              sx={{ color: isDarkMode ? 'white' : 'gray' }}
            >
              <TemporaryChatIcon size={20} active={isTemporary} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Exit Fullscreen" arrow>
          <IconButton size="small" onClick={handleToggleMaximize} sx={{ color: isDarkMode ? 'white' : 'gray' }}><FullscreenExit /></IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  const VoiceOnboardingPanel = (
    <VoiceOnboarding
      isDarkMode={isDarkMode}
      onClose={() => setShowVoiceOnboarding(false)}
      onContinue={handleContinueVoice}
    />
  );

  const TemporaryOnboardingPanel = (
    <TemporaryOnboarding
      isDarkMode={isDarkMode}
      onClose={() => setShowTemporaryOnboarding(false)}
      onContinue={handleContinueTemporary}
    />
  );

  const ChatContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: isDarkMode ? CHAT_GPT_LIGHT_DARK : '#ffffff', position: 'relative', overflow: 'hidden' }}>
      {maximized ? DesktopHeader : MobileHeader}

      {showVoiceOnboarding && VoiceOnboardingPanel}
      {showTemporaryOnboarding && TemporaryOnboardingPanel}

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Local Drawer for Small Window */}
        <AnimatePresence>
          {drawerOpen && (
            <>
              {/* Overlay for local drawer */}
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  zIndex: 100,
                  cursor: 'pointer'
                }}
              />
              {/* Drawer Content */}
              <Box
                component={motion.div}
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 280,
                  bgcolor: isDarkMode ? CHAT_GPT_DARK : '#ffffff',
                  zIndex: 101,
                  boxShadow: '4px 0 12px rgba(0,0,0,0.1)'
                }}
              >
                {SidebarContent}
              </Box>
            </>
          )}
        </AnimatePresence>

        {maximized && desktopSidebarOpen && (
          <Box sx={{ width: 260, bgcolor: isDarkMode ? CHAT_GPT_DARK : '#ffffff', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', borderRight: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
            {SidebarContent}
          </Box>
        )}


        {maximized && !desktopSidebarOpen && SidebarSlim}

        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: (maximized && messages.length === 0) ? 'center' : 'flex-end',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Scroll Area Wrapper */}
          <Box
            sx={{
              position: 'relative',
              flex: (maximized && messages.length === 0) ? 'none' : 1,
              display: (maximized && messages.length === 0) ? 'none' : 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Scroll Area for Messages or Mobile Suggestions */}
            <Box 
              ref={scrollRef} 
              onScroll={handleScroll}
              sx={{ 
                flex: 1, 
                overflowY: 'auto'
              }}
            >
              {messages.length > 0 ? (
                <>
                  {messages.map(renderMessage)}
                </>
              ) : (
                !maximized && (
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Stack spacing={1} sx={{ mt: 'auto', mb: 1, px: 2 }}>
                      {(!isImageMode && !isWriteMode && !isSearchMode) && SUGGESTIONS.map((item, index) => (
                        <Box
                          key={index}
                          onClick={() => {
                            if (item.text === 'Create an image') {
                              setIsImageMode(true);
                              setIsWriteMode(false);
                              setIsSearchMode(false);
                            } else if (item.text === 'Write or edit') {
                              setIsWriteMode(true);
                              setIsImageMode(false);
                              setIsSearchMode(false);
                            } else if (item.text === 'Look something up') {
                              setIsSearchMode(true);
                              setIsImageMode(false);
                              setIsWriteMode(false);
                            } else {
                              setInput(item.text);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 0.5,
                            px: 1,
                            cursor: 'pointer',
                            color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#555',
                            '&:hover': { color: isDarkMode ? '#fff' : '#000' }
                          }}
                        >
                          <Box sx={{ display: 'flex', opacity: 0.8 }}>{item.icon}</Box>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{item.text}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )
              )}
            </Box>

            {/* Scroll to latest — shown when user has scrolled up */}
            <Fade in={messages.length > 0 && !isAtBottom} unmountOnExit>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 5,
                  pointerEvents: 'none',
                }}
              >
                <Tooltip title="Scroll to latest message" arrow placement="top">
                  <IconButton
                    aria-label="Scroll to latest message"
                    onClick={() => pinToBottom('smooth')}
                    size="small"
                    sx={{
                      pointerEvents: 'auto',
                      width: 36,
                      height: 36,
                      bgcolor: isDarkMode ? '#2f2f2f' : '#ffffff',
                      color: isDarkMode ? '#ececec' : '#0d0d0d',
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
                      }`,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                      transition: 'background-color 0.2s, transform 0.15s',
                      '&:hover': {
                        bgcolor: isDarkMode ? '#3a3a3a' : '#f4f4f4',
                        transform: 'scale(1.04)',
                      },
                    }}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Fade>
          </Box>

          {/* Banner & sticky composer (messages scroll behind this) */}
          <Box
            sx={{
              width: '100%',
              flexShrink: 0,
              bgcolor: isDarkMode ? CHAT_GPT_LIGHT_DARK : '#ffffff',
              position: 'relative',
              zIndex: 2,
            }}
          >
            {maximized && messages.length === 0 && (
              <Box sx={{ mb: 4, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {isTemporary ? (
                  <>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: isDarkMode ? 'white' : 'black', mb: 1.5 }}>
                      Temporary Chat
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'gray', maxWidth: 500, mx: 'auto', lineHeight: 1.6 }}>
                      This chat won't appear in your chat history, and won't be used to train our models.
                    </Typography>
                  </>
                ) : (isImageMode || isWriteMode || isSearchMode) ? (
                  <Typography variant="h4" fontWeight="500" sx={{ color: isDarkMode ? 'white' : 'black', mb: 1, letterSpacing: '-0.5px' }}>
                    What's on your mind today?
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ width: 50, height: 50, mx: 'auto', mb: 2 }}>
                      <Lottie animationData={robotIcon} loop={true} />
                    </Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: isDarkMode ? 'white' : 'black' }}>
                      How can I help you today?
                    </Typography>
                  </>
                )}
              </Box>
            )}

            <Box sx={{ p: 1, pb: 2, pt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                component="p"
                sx={{
                  width: '100%',
                  maxWidth: maximized ? 720 : '340px',
                  mx: 'auto',
                  mb: 1,
                  textAlign: 'center',
                  color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  userSelect: 'none',
                }}
              >
                Sefi can make mistakes. Check important info.
              </Typography>
              {/* Image Mode Suggestions (Minimized View) */}
              {isImageMode && !maximized && messages.length === 0 && (
                <Box 
                  key="suggestions-mobile-image"
                  sx={{ 
                    bgcolor: isDarkMode ? '#2f2f2f' : '#f9f9f9',
                    borderRadius: '24px',
                    p: 1.5,
                    mb: 1,
                    width: '100%',
                    maxWidth: '340px',
                    mx: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isDarkMode ? 'white' : 'black', fontSize: '0.95rem' }}>
                      Create an image
                    </Typography>
                    <Stack direction="row" spacing={0.2} alignItems="center">
                      <Typography variant="caption" sx={{ color: 'gray', mr: 0.5 }}>What's new</Typography>
                      <IconButton 
                        size="small" 
                        sx={{ color: 'gray', p: 0.2 }}
                        onClick={() => document.getElementById('mobile-scroll-container')?.scrollBy({ left: -150, behavior: 'smooth' })}
                      >
                        <ChevronLeft sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ color: 'gray', p: 0.2 }}
                        onClick={() => document.getElementById('mobile-scroll-container')?.scrollBy({ left: 150, behavior: 'smooth' })}
                      >
                        <ChevronRight sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Box 
                    id="mobile-scroll-container"
                    ref={exploreScrollRefMobile}
                    sx={{ 
                      display: 'flex', 
                      gap: 1.2, 
                      overflowX: 'auto', 
                      pb: 0.5,
                      scrollbarWidth: 'none', 
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    <Box 
                      onClick={openFilePicker}
                      sx={{ 
                        flex: '0 0 130px',
                        borderRadius: '16px', 
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f4f4f4',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        aspectRatio: '1/1',
                        cursor: 'pointer'
                      }}>
                      <Add sx={{ fontSize: 24, mb: 1, color: isDarkMode ? 'white' : 'black' }} />
                      <Typography sx={{ fontSize: '0.7rem', color: isDarkMode ? 'white' : 'black', textAlign: 'center' }}>Upload a photo</Typography>
                    </Box>
                    {[
                      { title: 'Improve Your Desk Setup', img: 'https://images.unsplash.com/photo-1493932484895-752d1471eab5?w=200&h=200&fit=crop' },
                      { title: 'Wanderlust', img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=200&h=200&fit=crop' },
                      { title: 'Scribble', img: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=200&h=200&fit=crop' },
                      { title: 'Animal infographic', img: 'https://images.unsplash.com/photo-1546182990-dffeafbe841d?w=200&h=200&fit=crop' }
                    ].map((idea, i) => (
                      <Box key={i} sx={{ 
                        flex: '0 0 130px',
                        borderRadius: '16px', 
                        overflow: 'hidden', 
                        position: 'relative', 
                        aspectRatio: '1/1',
                        cursor: 'pointer'
                      }}>
                        <img src={idea.img} alt={idea.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <Box sx={{ 
                          position: 'absolute', 
                          inset: 0, 
                          bgcolor: 'rgba(0,0,0,0.3)', 
                          display: 'flex', 
                          alignItems: 'flex-end', 
                          p: 1.2,
                          opacity: 0.9
                        }}>
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>{idea.title}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Search Mode Suggestions (Minimized View) */}
              {isSearchMode && !maximized && messages.length === 0 && (
                <Box 
                  key="suggestions-mobile-search"
                  sx={{ 
                    bgcolor: isDarkMode ? '#2f2f2f' : '#f9f9f9',
                    borderRadius: '24px',
                    p: 2,
                    mb: 1,
                    width: '100%',
                    maxWidth: '340px',
                    mx: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ color: isDarkMode ? 'white' : 'black', fontWeight: 600, fontSize: '0.95rem' }}>Look something up</Typography>
                    <IconButton size="small" onClick={() => setIsSearchMode(false)}>
                      <Close sx={{ fontSize: 18, color: 'gray' }} />
                    </IconButton>
                  </Box>
                  <Stack spacing={1.5}>
                    {['Petrol Price Today', 'Sslc Result 2026', 'Dhurandhar 2 Ott', 'Iran News'].map((text, i) => (
                      <Box 
                        key={i} 
                        onClick={() => setInput(text)}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          cursor: 'pointer', 
                          color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', 
                          '&:hover': { color: isDarkMode ? 'white' : 'black' } 
                        }}>
                        <TrendingUp sx={{ fontSize: 18, opacity: 0.7 }} />
                        <Typography sx={{ fontSize: '0.9rem' }}>{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Write Mode Suggestions (Minimized View) */}
              {isWriteMode && !maximized && messages.length === 0 && (
                <Box 
                  key="suggestions-mobile-write"
                  sx={{ 
                    bgcolor: isDarkMode ? '#2f2f2f' : '#f9f9f9',
                    borderRadius: '24px',
                    p: 2,
                    mb: 1,
                    width: '100%',
                    maxWidth: '340px',
                    mx: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ color: isDarkMode ? 'white' : 'black', fontWeight: 600, fontSize: '0.95rem' }}>Write or edit</Typography>
                    <IconButton size="small" onClick={() => setIsWriteMode(false)}>
                      <Close sx={{ fontSize: 18, color: 'gray' }} />
                    </IconButton>
                  </Box>
                  <Stack spacing={1.5}>
                    {[
                      'Simplify my wording',
                      'Shorten without losing meaning',
                      'Proofread my writing',
                      'Expand with more detail'
                    ].map((text, i) => (
                      <Box 
                        key={i} 
                        onClick={() => setInput(text)}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          cursor: 'pointer', 
                          color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', 
                          '&:hover': { color: isDarkMode ? 'white' : 'black' } 
                        }}>
                        <Edit sx={{ fontSize: 18, opacity: 0.7 }} />
                        <Typography sx={{ fontSize: '0.9rem' }}>{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              <Box sx={{
                width: '100%',
                maxWidth: maximized ? 720 : '340px',
                mx: 'auto',
                bgcolor: isDarkMode ? '#2f2f2f' : '#f4f4f4',
                borderRadius: !maximized ? '24px' : '28px',
                p: !maximized ? 1.5 : 1.2,
                pr: isConversationalMode ? (maximized ? 6.5 : 5.5) : (!maximized ? 1.5 : 1.2),
                border: isImageMode && maximized
                  ? (isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)')
                  : (isDarkMode ? '1px solid rgba(255,255,255,0.06)' : 'none'),
                boxShadow: !maximized ? '0 4px 12px rgba(0,0,0,0.1)' : (isImageMode ? '0 4px 20px rgba(0,0,0,0.08)' : 'none'),
                position: 'relative',
                mb: !maximized ? 1 : 0,
                transition: 'border-radius 0.2s ease, background-color 0.2s ease',
              }}>
                {renderConversationalCloseButton({ size: maximized ? 36 : 32 })}
                
                {voiceError && (
                  <Box 
                    sx={{ 
                      bgcolor: '#ffebee', 
                      color: '#c62828', 
                      px: 1.5, 
                      py: 0.5, 
                      borderRadius: '12px', 
                      mb: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      fontSize: '0.85rem'
                    }}
                  >
                    <Typography variant="body2" style={{ color: '#c62828', fontSize: '0.85rem' }}>{voiceError}</Typography>
                    <IconButton size="small" onClick={() => setVoiceError(null)} style={{ color: '#c62828', padding: 2 }}>
                      <Close style={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}

                {/* Stacked Layout (Modes) or Single Row Layout (Normal) */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: (isImageMode || isSearchMode) ? 'column' : 'row', 
                  alignItems: (isImageMode || isSearchMode) ? 'stretch' : 'center', 
                  flex: 1, 
                  gap: (isImageMode || isSearchMode) ? 1 : 0.5 
                }}>

                  <Menu
                    anchorEl={plusAnchor}
                    open={Boolean(plusAnchor)}
                    onClose={() => setPlusAnchor(null)}
                    disablePortal={!maximized}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={{ zIndex: 2000 }}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        borderRadius: '20px',
                        minWidth: 240,
                        bgcolor: isDarkMode ? '#2f2f2f' : 'white',
                        color: isDarkMode ? 'white' : 'black',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                        boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        zIndex: 2000
                      }
                    }}
                  >
                    <Box sx={{ py: 0.5 }}>
                      {[
                        { 
                          icon: <AttachFile sx={{ fontSize: 18 }} />, 
                          text: 'Add photos & files', 
                          action: () => openFilePicker('document')
                        },
                        { 
                          icon: <Schema sx={{ fontSize: 18 }} />, 
                          text: 'Blueprint', 
                          action: () => openFilePicker('blueprint')
                        },
                        { 
                          icon: <History sx={{ fontSize: 18 }} />, 
                          text: 'Recent files', 
                          action: (e) => setRecentFilesAnchor(e.currentTarget), 
                          hasSub: true 
                        },
                        { type: 'divider' },
                        { icon: <Image sx={{ fontSize: 18 }} />, text: 'Create image', action: () => { setIsImageMode(true); setIsSearchMode(false); setIsWriteMode(false); } },
                        { icon: <Explore sx={{ fontSize: 18 }} />, text: 'Deep research', action: () => { setIsSearchMode(true); setIsImageMode(false); setIsWriteMode(false); } },
                        { icon: <Language sx={{ fontSize: 18 }} />, text: 'Web search', action: () => { setIsSearchMode(true); setIsImageMode(false); setIsWriteMode(false); } },
                        { icon: <MoreHoriz sx={{ fontSize: 18 }} />, text: 'More', action: () => {}, hasSub: true },
                        { type: 'divider' },
                        { icon: <FolderOpen sx={{ fontSize: 18 }} />, text: 'New project', action: handleOpenNewProjectDialog, hasSub: false },
                      ].map((item, idx) => {
                        if (item.type === 'divider') return <Divider key={idx} sx={{ my: 0.5, opacity: 0.5 }} />;
                        
                        return (
                          <MenuItem 
                            key={idx}
                            onClick={(e) => {
                              item.action(e);
                              if (!item.hasSub) {
                                setPlusAnchor(null);
                              }
                            }}
                            sx={{ 
                              py: 1.2, 
                              px: 2, 
                              display: 'flex',
                              justifyContent: 'space-between',
                              '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                              {item.icon}
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{item.text}</Typography>
                            </Box>
                            {item.hasSub && <ChevronRight sx={{ fontSize: 16, color: 'gray' }} />}
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Menu>
                  
                  <Menu
                    anchorEl={recentFilesAnchor}
                    open={Boolean(recentFilesAnchor)}
                    onClose={() => setRecentFilesAnchor(null)}
                    disablePortal={!maximized}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={{ zIndex: 2100 }}
                    PaperProps={{
                      sx: {
                        ml: 1,
                        borderRadius: '16px',
                        minWidth: 220,
                        bgcolor: isDarkMode ? '#2f2f2f' : 'white',
                        color: isDarkMode ? 'white' : 'black',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        zIndex: 2100
                      }
                    }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography sx={{ color: 'gray', fontSize: '0.75rem', fontWeight: 600 }}>RECENT FILES</Typography>
                    </Box>
                    {recentFiles.map((file, idx) => (
                      <MenuItem 
                        key={idx}
                        onClick={() => {
                          setSelectedFiles(prev => [...prev, { id: Date.now(), url: file.url, name: file.name, type: 'image/png' }]);
                          setRecentFilesAnchor(null);
                          setPlusAnchor(null);
                        }}
                        sx={{ 
                          py: 1, 
                          px: 2, 
                          gap: 1.5,
                          '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
                        }}
                      >
                        <Box 
                          component="img" 
                          src={file.url} 
                          sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'cover' }} 
                        />
                        <Typography sx={{ fontSize: '0.85rem' }}>{file.name}</Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: maximized ? 'row' : 'column',
                    alignItems: maximized ? 'center' : 'flex-start',
                    flex: 1, 
                    gap: 1 
                  }}>
                    {selectedFiles.length > 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: 1.5, 
                        p: maximized ? 0 : 1,
                        width: maximized ? 'auto' : '100%',
                        flexShrink: 0,
                      }}>
                        {selectedFiles.map((file) => (
                          <Tooltip key={file.id} title={file.uploadError || (file.uploading ? "Uploading..." : file.name)} arrow placement="top">
                            <Box sx={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
                              {file.url ? (
                                <Box 
                                  component="img" 
                                  src={file.url} 
                                  sx={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    borderRadius: '8px', 
                                    objectFit: 'cover', 
                                    border: file.uploadError 
                                      ? '2px solid #ef5350' 
                                      : (isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)') 
                                  }} 
                                />
                              ) : file.isVideo ? (
                                <Box sx={{
                                  width: '100%',
                                  height: '100%',
                                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#eee',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: file.uploadError ? '2px solid #ef5350' : 'none',
                                }}>
                                  <Videocam sx={{ fontSize: 18, color: file.uploadError ? '#ef5350' : 'inherit' }} />
                                </Box>
                              ) : (
                                <Box sx={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#eee', 
                                  borderRadius: '8px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  border: file.uploadError ? '2px solid #ef5350' : 'none'
                                }}>
                                  <AttachFile sx={{ fontSize: 16, color: file.uploadError ? '#ef5350' : 'inherit' }} />
                                </Box>
                              )}
                              {file.uploading && (
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  bgcolor: 'rgba(0,0,0,0.4)',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <CircularProgress size={16} sx={{ color: 'white' }} />
                                </Box>
                              )}
                              <IconButton 
                                size="small" 
                                onClick={() => setSelectedFiles(prev => prev.filter(f => f.id !== file.id))}
                                sx={{ 
                                  position: 'absolute', 
                                  top: -6, 
                                  right: -6, 
                                  bgcolor: isDarkMode ? 'white' : 'black', 
                                  color: isDarkMode ? 'black' : 'white', 
                                  width: 18,
                                  height: 18,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                  '&:hover': { bgcolor: isDarkMode ? '#eee' : '#333' } 
                                }}
                              >
                                <Close sx={{ fontSize: 10 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1, minWidth: 0 }}>
                      {(!isImageMode && !isSearchMode) && (
                        <Tooltip title="Add files and more" arrow>
                          <IconButton 
                            onClick={(e) => setPlusAnchor(e.currentTarget)}
                            size="small" 
                            sx={{ 
                              color: isDarkMode ? 'white' : 'gray',
                              '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }
                            }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                        <InputBase
                          inputRef={chatInputRef}
                          fullWidth
                          multiline
                          maxRows={8}
                          placeholder={isRecording ? "Listening..." : (isTranscribing ? "Transcribing..." : (isSearchMode ? "Search the web" : (isWriteMode ? "Write anything" : (isImageMode ? "Describe or edit an image" : "Ask anything"))))}
                          value={input}
                          disabled={sendMessage || isTranscribing}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && input === '' && (isImageMode || isSearchMode || isWriteMode)) {
                              setIsImageMode(false);
                              setIsSearchMode(false);
                              setIsWriteMode(false);
                            }
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSend();
                            }
                          }}
                          sx={{
                            ml: (maximized && (isImageMode || isSearchMode)) ? 0.5 : 1,
                            px: (maximized && (isImageMode || isSearchMode)) ? 0.5 : 0,
                            color: isDarkMode ? 'white' : 'black',
                            fontSize: '1.05rem',
                            flex: 1,
                            '& .MuiInputBase-input::placeholder': {
                              color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                              opacity: 1,
                            },
                          }}
                        />
                      </Box>
                    </Box>

                  {(!isImageMode && !isSearchMode) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      {sendMessage ? (
                        <Tooltip title="Stop generating" arrow>
                          <IconButton
                            onClick={handleStopGeneration}
                            size="small"
                            sx={{
                              bgcolor: isDarkMode ? 'white' : '#000',
                              color: isDarkMode ? 'black' : 'white',
                              width: 34,
                              height: 34,
                              '&:hover': { bgcolor: isDarkMode ? '#eee' : '#333' },
                            }}
                          >
                            <Stop sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        !input.trim() && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {!isTemporary && (
                              <>
                                <Tooltip title={isRecording ? 'Stop recording' : 'Voice input'} arrow>
                                  <IconButton
                                    onClick={handleMicClick}
                                    disabled={isTranscribing}
                                    size="small"
                                    sx={{
                                      color: isRecording ? '#fff' : (isDarkMode ? 'white' : 'gray'),
                                      bgcolor: isRecording ? '#ef5350' : 'transparent',
                                      width: 34,
                                      height: 34,
                                      '&:hover': {
                                        bgcolor: isRecording ? '#e53935' : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
                                      },
                                      animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                                      '@keyframes pulse': {
                                        '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(239, 83, 80, 0.7)' },
                                        '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 6px rgba(239, 83, 80, 0)' },
                                        '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(239, 83, 80, 0)' },
                                      },
                                    }}
                                  >
                                    {isTranscribing ? (
                                      <CircularProgress size={18} sx={{ color: isDarkMode ? 'white' : 'gray' }} />
                                    ) : (
                                      <Mic fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                {renderVoiceWaveformControl({ waveSize: 22, btnSize: 34 })}
                              </>
                            )}
                          </Stack>
                        )
                      )}
                      {(input.trim() || selectedFiles.length > 0 || maximized) && !sendMessage && (
                        <IconButton
                          onClick={handleSend}
                          disabled={!input.trim() && selectedFiles.length === 0}
                          sx={{
                            bgcolor: input.trim() ? (isDarkMode ? 'white' : 'black') : 'transparent',
                            color: input.trim() ? (isDarkMode ? 'black' : 'white') : 'gray',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: input.trim() ? (isDarkMode ? '#eee' : '#333') : 'transparent' },
                          }}
                        >
                          <ArrowUpward sx={{ fontSize: 20 }} />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                </Box>

                {/* Bottom Row (For Image or Search Mode) - Enabled for all views for mobile consistency */}
                {(isImageMode || isSearchMode) && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: maximized ? 1.5 : 1,
                    px: maximized ? 0.5 : 0
                  }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton 
                        onClick={openFilePicker}
                        size="small" 
                        sx={{ color: isDarkMode ? 'white' : 'gray' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                      
                      {isImageMode && (
                        <>
                          <Box 
                            onClick={() => setIsImageMode(false)}
                            onMouseEnter={() => setHoverImage(true)}
                            onMouseLeave={() => setHoverImage(false)}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.8, 
                              px: 1.2, 
                              py: 0.6, 
                              borderRadius: '20px', 
                              color: '#3498db', 
                              cursor: 'pointer', 
                              bgcolor: isDarkMode ? 'rgba(52,152,219,0.1)' : 'rgba(52,152,219,0.05)',
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: isDarkMode ? 'rgba(52,152,219,0.15)' : 'rgba(52,152,219,0.1)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}>
                              {hoverImage ? (
                                <Box sx={{ bgcolor: isDarkMode ? 'rgba(52,152,219,0.2)' : 'rgba(52,152,219,0.1)', borderRadius: '50%', p: 0.2, display: 'flex' }}>
                                  <Close sx={{ fontSize: 14 }} />
                                </Box>
                              ) : (
                                <Image sx={{ fontSize: 16 }} />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Image</Typography>
                          </Box>
                          <Box 
                            onClick={(e) => setAspectRatioAnchor(e.currentTarget)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '12px', border: 'none', color: isDarkMode ? 'white' : 'black', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
                            <AutoAwesome sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{selectedRatio}</Typography>
                            <KeyboardArrowDown sx={{ fontSize: 14 }} />
                          </Box>
                          <Menu
                            anchorEl={aspectRatioAnchor}
                            open={Boolean(aspectRatioAnchor)}
                            onClose={() => setAspectRatioAnchor(null)}
                            sx={{ zIndex: 2500 }}
                            PaperProps={{
                              sx: {
                                mt: 1,
                                borderRadius: '16px',
                                minWidth: 200,
                                bgcolor: isDarkMode ? '#2f2f2f' : 'white',
                                color: isDarkMode ? 'white' : 'black',
                                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                                zIndex: 2500
                              }
                            }}
                          >
                            <Box sx={{ px: 2, py: 1.5 }}>
                              <Typography sx={{ color: 'gray', fontSize: '0.85rem' }}>Choose image aspect ratio</Typography>
                            </Box>
                            {[
                              { label: 'Auto', sub: '', icon: <AutoAwesome fontSize="small" /> },
                              { label: 'Square', sub: '1:1', icon: <CropSquare fontSize="small" /> },
                              { label: 'Portrait', sub: '3:4', icon: <CropPortrait fontSize="small" /> },
                              { label: 'Story', sub: '9:16', icon: <StayCurrentPortrait fontSize="small" /> },
                              { label: 'Landscape', sub: '4:3', icon: <CropLandscape fontSize="small" /> },
                              { label: 'Widescreen', sub: '16:9', icon: <Crop75 fontSize="small" /> },
                            ].map((ratio) => (
                              <MenuItem 
                                key={ratio.label}
                                onClick={() => {
                                  setSelectedRatio(ratio.label);
                                  setAspectRatioAnchor(null);
                                }}
                                sx={{ 
                                  py: 1, 
                                  px: 2, 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {ratio.icon}
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{ratio.label}</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'gray' }}>{ratio.sub}</Typography>
                                  </Box>
                                </Box>
                                {selectedRatio === ratio.label && <Check sx={{ fontSize: 16 }} />}
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}

                      {isSearchMode && (
                        <Box 
                          onClick={() => setIsSearchMode(false)}
                          onMouseEnter={() => setHoverSearch(true)}
                          onMouseLeave={() => setHoverSearch(false)}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.8, 
                            px: 1.2, 
                            py: 0.6, 
                            borderRadius: '20px', 
                            color: '#007aff', 
                            cursor: 'pointer', 
                            bgcolor: isDarkMode ? 'rgba(0,122,255,0.1)' : 'rgba(0,122,255,0.05)',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: isDarkMode ? 'rgba(0,122,255,0.15)' : 'rgba(0,122,255,0.1)' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}>
                            {hoverSearch ? (
                              <Box sx={{ bgcolor: isDarkMode ? 'rgba(0,122,255,0.2)' : 'rgba(0,122,255,0.1)', borderRadius: '50%', p: 0.2, display: 'flex' }}>
                                <Close sx={{ fontSize: 14 }} />
                              </Box>
                            ) : (
                              <Language sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Search</Typography>
                        </Box>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={maximized ? 1.5 : 0.8} alignItems="center">
                      {maximized && (
                        <Tooltip title={<Typography sx={{ fontSize: '0.75rem', py: 0.2, px: 0.5 }}>Select model</Typography>} arrow placement="top">
                          <Box 
                            onClick={(e) => setModelAnchor(e.currentTarget)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '12px', color: 'gray', bgcolor: 'transparent', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { color: isDarkMode ? 'white' : 'black' } }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{selectedModel}</Typography>
                            <KeyboardArrowDown sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      )}
                      
                      {sendMessage ? (
                        <Tooltip title="Stop generating" arrow>
                          <IconButton
                            onClick={handleStopGeneration}
                            sx={{
                              bgcolor: isDarkMode ? 'white' : 'black',
                              color: isDarkMode ? 'black' : 'white',
                              width: maximized ? 42 : 36,
                              height: maximized ? 42 : 36,
                              border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : 'none',
                              '&:hover': { bgcolor: isDarkMode ? '#eee' : '#333' },
                            }}
                          >
                            <Stop sx={{ fontSize: maximized ? 22 : 18 }} />
                          </IconButton>
                        </Tooltip>
                      ) : !input.trim() ? (
                        <>
                          {!isTemporary && (
                            <>
                              <Tooltip title={isRecording ? 'Stop recording' : 'Voice input'} arrow>
                                <IconButton
                                  onClick={handleMicClick}
                                  disabled={isTranscribing}
                                  size="small"
                                  sx={{
                                    color: isRecording ? '#fff' : (isDarkMode ? 'white' : 'gray'),
                                    bgcolor: isRecording ? '#ef5350' : 'transparent',
                                    width: maximized ? 42 : 36,
                                    height: maximized ? 42 : 36,
                                    '&:hover': {
                                      bgcolor: isRecording ? '#e53935' : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
                                    },
                                    animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                                    '@keyframes pulse': {
                                      '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(239, 83, 80, 0.7)' },
                                      '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 6px rgba(239, 83, 80, 0)' },
                                      '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(239, 83, 80, 0)' },
                                    },
                                  }}
                                >
                                  {isTranscribing ? (
                                    <CircularProgress size={maximized ? 22 : 18} sx={{ color: isDarkMode ? 'white' : 'gray' }} />
                                  ) : (
                                    <Mic sx={{ fontSize: maximized ? 22 : 20 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {renderVoiceWaveformControl({
                                waveSize: maximized ? 28 : 24,
                                btnSize: maximized ? 42 : 36,
                              })}
                            </>
                          )}
                        </>
                      ) : (
                        <IconButton
                          onClick={handleSend}
                          sx={{
                            bgcolor: isDarkMode ? 'white' : 'black',
                            color: isDarkMode ? 'black' : 'white',
                            width: maximized ? 32 : 30,
                            height: maximized ? 32 : 30,
                            '&:hover': { bgcolor: isDarkMode ? '#eee' : '#333' },
                          }}
                        >
                          <ArrowUpward sx={{ fontSize: maximized ? 20 : 18 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>

              {maximized && messages.length === 0 && !isImageMode && !isWriteMode && !isSearchMode && (
                <Stack
                  direction="row"
                  justifyContent="center"
                  spacing={1.5}
                  sx={{ mt: 3 }}
                >
                  {[
                    { icon: <Image sx={{ fontSize: 18 }} />, text: 'Create an image' },
                    { icon: <Edit sx={{ fontSize: 18 }} />, text: 'Write or edit' },
                    { icon: <Language sx={{ fontSize: 18 }} />, text: 'Look something up' }
                  ].map((action, idx) => (
                    <Box
                      key={idx}
                      onClick={() => {
                        if (action.text === 'Create an image') {
                          setIsImageMode(true);
                          setIsWriteMode(false);
                        } else if (action.text === 'Write or edit') {
                          setIsWriteMode(true);
                          setIsImageMode(false);
                          setIsSearchMode(false);
                        } else if (action.text === 'Look something up') {
                          setIsSearchMode(true);
                          setIsImageMode(false);
                          setIsWriteMode(false);
                        } else {
                          setInput(action.text);
                          setIsImageMode(false);
                          setIsWriteMode(false);
                          setIsSearchMode(false);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.8,
                        borderRadius: '20px',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                        cursor: 'pointer',
                        color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                          color: isDarkMode ? '#fff' : '#000'
                        }
                      }}
                    >
                      {action.icon}
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{action.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {isWriteMode && maximized && (
                <Box sx={{ maxWidth: 720, mx: 'auto', mt: 2, width: '100%' }}>
                  <Stack spacing={0.5}>
                    {[
                      'Simplify my wording',
                      'Shorten without losing meaning',
                      'Proofread my writing',
                      'Expand with more detail'
                    ].map((text, i) => (
                      <Box 
                        key={i}
                        onClick={() => {
                          setInput(text);
                          setIsWriteMode(false);
                        }}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          px: 2, 
                          py: 1.2, 
                          borderRadius: '12px', 
                          cursor: 'pointer',
                          color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          transition: 'all 0.2s',
                          '&:hover': { 
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            color: isDarkMode ? '#fff' : '#000'
                          } 
                        }}
                      >
                        <Edit sx={{ fontSize: 18, opacity: 0.7 }} />
                        <Typography sx={{ fontSize: '0.9rem' }}>{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {isSearchMode && maximized && (
                <Box sx={{ maxWidth: 720, mx: 'auto', mt: 2, width: '100%' }}>
                  <Stack spacing={0.5}>
                    {[
                      'Petrol Price Today',
                      'Sslc Result 2026',
                      'Dhurandhar 2 Ott',
                      'Iran News'
                    ].map((text, i) => (
                      <Box 
                        key={i}
                        onClick={() => {
                          setInput(text);
                          setIsSearchMode(false);
                        }}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          px: 2, 
                          py: 1.2, 
                          borderRadius: '12px', 
                          cursor: 'pointer',
                          color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          transition: 'all 0.2s',
                          '&:hover': { 
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            color: isDarkMode ? '#fff' : '#000'
                          } 
                        }}
                      >
                        <TrendingUp sx={{ fontSize: 18, opacity: 0.7 }} />
                        <Typography sx={{ fontSize: '0.9rem' }}>{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {isImageMode && messages.length === 0 && maximized && (
                <Box 
                  key="suggestions-desktop"
                  sx={{ mt: 6, width: '100%', maxWidth: 800, mx: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isDarkMode ? 'white' : 'black' }}>
                      Explore ideas
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ color: 'gray', borderBottom: '1px dotted gray', cursor: 'pointer' }}>What's new</Typography>
                      <IconButton 
                        size="small" 
                        sx={{ color: 'gray' }}
                        onClick={() => document.getElementById('desktop-scroll-container')?.scrollBy({ left: -200, behavior: 'smooth' })}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ color: 'gray' }}
                        onClick={() => document.getElementById('desktop-scroll-container')?.scrollBy({ left: 200, behavior: 'smooth' })}
                      >
                        <ChevronRight />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Box 
                    id="desktop-scroll-container"
                    ref={exploreScrollRefDesktop}
                    sx={{ 
                      display: 'flex', 
                      gap: 1.5, 
                      overflowX: 'auto', 
                      pb: 1,
                      scrollbarWidth: 'none', 
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    <Box 
                      onClick={openFilePicker}
                      sx={{ 
                        flex: '0 0 calc((100% - 60px) / 5.2)',
                        minWidth: '130px',
                        borderRadius: '16px', 
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f4f4f4',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        aspectRatio: '1/1',
                        cursor: 'pointer',
                        border: isDarkMode ? '1px dashed rgba(255,255,255,0.1)' : '1px dashed rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)' }
                      }}>
                      <Add sx={{ fontSize: 32, mb: 1, color: isDarkMode ? 'white' : 'black' }} />
                      <Typography sx={{ fontSize: '0.85rem', color: isDarkMode ? 'white' : 'black', fontWeight: 500 }}>Upload a photo</Typography>
                    </Box>
                    {[
                      { title: 'Improve Your Desk Setup', img: 'https://images.unsplash.com/photo-1493932484895-752d1471eab5?w=200&h=200&fit=crop' },
                      { title: 'Wanderlust', img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=200&h=200&fit=crop' },
                      { title: 'Scribble', img: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=200&h=200&fit=crop' },
                      { title: 'Animal infographic', img: 'https://images.unsplash.com/photo-1546182990-dffeafbe841d?w=200&h=200&fit=crop' },
                      { title: 'Architectural Marvels', img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200&h=200&fit=crop' }
                    ].map((idea, i) => (
                      <Box key={i} sx={{ 
                        flex: '0 0 calc((100% - 60px) / 5.2)',
                        minWidth: '130px',
                        borderRadius: '16px', 
                        overflow: 'hidden', 
                        position: 'relative', 
                        aspectRatio: '1/1',
                        cursor: 'pointer'
                      }}>
                        <img src={idea.img} alt={idea.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <Box sx={{ 
                          position: 'absolute', 
                          inset: 0, 
                          bgcolor: 'rgba(0,0,0,0.3)', 
                          display: 'flex', 
                          alignItems: 'flex-end', 
                          p: 1.5,
                          opacity: 0.8
                        }}>
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>{idea.title}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {isTemporary && !maximized && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    mb: 2,
                    color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'gray',
                    fontSize: '0.65rem'
                  }}
                >
                  For safety, we may keep a copy of this chat for upto 30 days.
                </Typography>
              )}
          </Box>
          {isTemporary && maximized && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 0,
                right: 0,
                textAlign: 'center',
                color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'gray',
                fontSize: '0.7rem',
                zIndex: 10
              }}
            >
              For safety, we may keep a copy of this chat for upto 30 days.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  </Box>
);

  const ModelSelectorMenu = (
    <Menu
      anchorEl={modelAnchor}
      open={Boolean(modelAnchor)}
      onClose={() => setModelAnchor(null)}
      PaperProps={{
        sx: {
          mt: 1,
          borderRadius: '16px',
          minWidth: 160,
          bgcolor: isDarkMode ? '#2f2f2f' : 'white',
          color: isDarkMode ? 'white' : 'black',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }
      }}
    >
      <Box sx={{ px: 2, py: 1 }}>
        <Typography sx={{ color: 'gray', fontSize: '0.75rem' }}>Latest • 5.5</Typography>
      </Box>
      {['Instant', 'Thinking'].map((model) => (
        <MenuItem 
          key={model}
          onClick={() => {
            setSelectedModel(model);
            setModelAnchor(null);
          }}
          sx={{ 
            py: 1, 
            px: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{model}</Typography>
          {selectedModel === model && <Check sx={{ fontSize: 16 }} />}
        </MenuItem>
      ))}
    </Menu>
  );

  const SearchModalContent = (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: isDarkMode ? '#2f2f2f' : '#ffffff' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        px: 2, 
        py: maximized ? 2 : 1.5,
        borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
      }}>
        <InputBase
          autoFocus
          placeholder="Search chats..."
          value={searchModalQuery}
          onChange={(e) => setSearchModalQuery(e.target.value)}
          sx={{
            flex: 1,
            fontSize: maximized ? '1.1rem' : '1rem',
            color: isDarkMode ? 'white' : 'black',
            '& .MuiInputBase-input::placeholder': {
              color: isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
              opacity: 1
            }
          }}
        />
        <IconButton size="small" onClick={() => setSearchDialogOpen(false)} sx={{ color: 'gray', opacity: 0.7 }}>
          <Close />
        </IconButton>
      </Box>

      {/* List Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        p: maximized ? 2 : 1.5,
        /* Custom Thin Scrollbar */
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { 
          bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          borderRadius: '10px'
        }
      }}>
        {/* New Chat Button (Mobile Style) */}
        <Button
          variant="text"
          startIcon={<Edit fontSize="small" />}
          fullWidth
          onClick={() => {
            handleNewChat();
            setSearchDialogOpen(false);
          }}
          sx={{
            justifyContent: 'flex-start',
            bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
            color: isDarkMode ? 'white' : 'black',
            mb: 3,
            borderRadius: '12px',
            py: 1.5,
            px: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }
          }}
        >
          New chat
        </Button>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontWeight: 600, ml: 1, mb: 1, display: 'block', fontSize: '0.75rem' }}>
            Recents
          </Typography>
          <Stack spacing={0.5}>
            {searchHistoryLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!searchHistoryLoading && searchVisibleSessions.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => {
                    handleLoadChat(item);
                    setSearchDialogOpen(false);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 1.5,
                    py: 1.2,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                    bgcolor: currentChatId === item.id ? (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
                    '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }
                  }}
                >
                  <ChatBubbleOutline sx={{ fontSize: 20, color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: maximized ? '0.95rem' : '0.9rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title}
                    </Typography>
                    {item.projectId && projectNameById[item.projectId] && (
                      <Typography sx={{ fontSize: '0.72rem', color: 'gray', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {projectNameById[item.projectId]}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            {!searchHistoryLoading && searchVisibleSessions.length === 0 && (
              <Typography align="center" sx={{ color: 'gray', py: 2, fontSize: '0.85rem' }}>
                {history.length === 0 ? 'No chats yet' : 'No conversations found'}
              </Typography>
            )}
          </Stack>
        </Box>

        <Box />
      </Box>
    </Box>
  );

  return (
    <>
      <AnimatePresence>
        {open && !maximized && (
          <ClickAwayListener onClickAway={(e) => {
            if (isSelectingFileRef.current || isSelectingFile) return;
            if (document.activeElement?.type === 'file') return;
            if (plusAnchor || recentFilesAnchor || aspectRatioAnchor || modelAnchor || recentsAnchor) return;
            if (e.target instanceof Element && e.target.closest('.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiTooltip-popper')) {
              return;
            }
            handleChatClose(e);
          }}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              sx={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                width: { xs: 'calc(100% - 40px)', sm: 400 },
                height: 600,
                zIndex: 1400,
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {searchDialogOpen ? (
                <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                  {SearchModalContent}
                </Box>
              ) : ChatContent}
            </Box>
          </ClickAwayListener>
        )}
      </AnimatePresence>

      <input
        type="file"
        hidden
        multiple
        ref={fileInputRef}
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.ppt,.pptx,.mp4,.mov,.webm,.mkv,.avi,video/mp4,video/quicktime,video/webm,video/x-matroska,video/x-msvideo"
        onChange={handleFileChange}
      />

      <Dialog
        fullScreen
        open={open && maximized}
        onClose={handleChatClose}
        TransitionComponent={Zoom}
      >
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {ChatContent}
          {/* Search Dialog for Maximized Mode */}
          <Dialog
            open={searchDialogOpen && maximized}
            onClose={() => setSearchDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            sx={{
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
                pt: { xs: 5, md: 10 }
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: '16px',
                bgcolor: isDarkMode ? '#2f2f2f' : '#ffffff',
                backgroundImage: 'none',
                overflow: 'hidden',
                boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
                maxHeight: '70vh'
              }
            }}
          >
            {SearchModalContent}
          </Dialog>
        </Box>
      </Dialog>
      <Dialog
        fullScreen
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        sx={{ 
          '& .MuiDialog-paper': { 
            bgcolor: 'rgba(0,0,0,0.95)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          } 
        }}
      >
        <IconButton 
          onClick={() => setPreviewImage(null)}
          sx={{ position: 'absolute', top: 20, right: 20, color: 'white' }}
        >
          <Close />
        </IconButton>
        <Box 
          component="img" 
          src={previewImage} 
          sx={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', borderRadius: '8px' }} 
        />
      </Dialog>
      {ModelSelectorMenu}

      <Dialog
        open={projectDialogOpen}
        onClose={() => !projectSaving && setProjectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: isDarkMode ? '#2f2f2f' : '#ffffff',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? 'white' : 'black', pb: 1 }}>
          New project
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
            Group related chats together under one project.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateProject();
            }}
            disabled={projectSaving}
            variant="outlined"
            size="small"
            InputLabelProps={{ style: { color: isDarkMode ? 'rgba(255,255,255,0.7)' : undefined } }}
            inputProps={{ style: { color: isDarkMode ? 'white' : 'black' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProjectDialogOpen(false)} disabled={projectSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={!newProjectName.trim() || projectSaving}
          >
            {projectSaving ? 'Creating…' : 'Create project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteChatDialogOpen}
        onClose={handleCancelDeleteChat}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: isDarkMode ? '#2f2f2f' : '#ffffff',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? 'white' : 'black', pb: 1 }}>
          Delete chat?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'gray', mb: 1.5 }}>
            This will permanently delete this conversation and cannot be undone.
          </Typography>
          {chatToDelete?.title && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: isDarkMode ? 'white' : 'black',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {chatToDelete.title}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDeleteChat} disabled={!!deletingChatId}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteChat}
            variant="contained"
            color="secondary"
            disabled={!!deletingChatId}
            sx={{
              bgcolor: '#d32f2f',
              color: '#fff',
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {deletingChatId ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteProjectDialog
        open={deleteProjectDialogOpen}
        project={projectToDelete}
        isDarkMode={isDarkMode}
        deleting={!!deletingProjectId}
        error={deleteProjectError}
        onCancel={handleCancelDeleteProject}
        onConfirm={handleConfirmDeleteProject}
      />

    </>
  );
}
